import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import {
    Search,
    Plus,
    Filter,
    MoreVertical,
    ArrowLeft,
    FileText,
    File,
    User,
    Mail,
    Phone,
    Briefcase,
    AlertCircle,
    CheckCircle2,
    Clock,
    Download,
    Upload,
    Folder as FolderIcon,
    Trash2,
    Share2,
    Loader2,
    Edit2,
    Box,
    Eye,
    BarChart as BarChartIcon,
    Scale,
    PieChart as PieChartIcon,
    ChevronRight,
    ChevronDown,
    Layers,
    FolderOpen,
    Tag,
    RefreshCw,
    Zap,
    Users,
    BookUser,
    Lock,
    Settings,
    Receipt as ReceiptIcon,
    Star,
    Play,
    Pause
} from "lucide-react";
import { getVigenteStatusInfo } from "@/lib/processStatus";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { useDespachos, Deadline, Receipt, parseLocalDate } from "@/hooks/useDespachos";
import * as scraperApi from "@/services/scraperApi";
import { jsPDF } from "jspdf";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import * as legalEmailsApi from "@/services/legalEmailsApi";
import { DocumentEditor } from "../components/DocumentEditor";
import { PdfViewer } from "../components/PdfViewer";
import { ImageViewer } from "../components/ImageViewer";
import { ProcesosDetalleSheet } from "../components/ProcesosDetalleSheet";
import { CalculadoraTerminos } from "../components/CalculadoraTerminos";
import CalendarioProcesalDespachos from "../components/CalendarioProcesalDespachos";
import { cn } from "@/lib/utils";

import { PageHeader } from "../components/PageHeader";

const SearchBar = ({ value, onChange, placeholder = "Buscar..." }: { value?: string, onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void, placeholder?: string }) => (
    <div className="relative w-full md:w-72">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input className="pl-9 rounded-xl border-gray-200 focus:border-legal-blue focus:ring-legal-blue/20 h-10 w-full" placeholder={placeholder} value={value} onChange={onChange} />
    </div>
);

import { useNavigate, useParams, useLocation } from "react-router-dom";
import { SamaiJudicialPage } from "./JudicialSearchPage";

// --- Clientes Page ---

export const ClientesPage = () => {
    const { clients, loading, addClient, updateClient, deleteClient } = useDespachos();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [clientForm, setClientForm] = useState({ name: "", email: "", phone: "", status: "active" as "active" | "inactive" });
    const { toast } = useToast();

    const filteredClients = clients.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleEdit = (client: any) => {
        setEditingId(client.id);
        setClientForm({ name: client.name, email: client.email || "", phone: client.phone || "", status: client.status });
        setIsDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!clientForm.name) {
            toast({ title: "Error", description: "El nombre es obligatorio", variant: "destructive" });
            return;
        }
        if (editingId) {
            await updateClient(editingId, clientForm);
        } else {
            await addClient(clientForm);
        }
        setIsDialogOpen(false);
        setEditingId(null);
        setClientForm({ name: "", email: "", phone: "", status: "active" });
    };

    return (
        <div className="animate-fade-in-up">
            <PageHeader
                title="Clientes"
                description="Gestiona tu base de datos de clientes"
                icon={Users}
                action={
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15">
                                <Plus className="mr-2 h-4 w-4" /> Nuevo Cliente
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-2xl">
                            <DialogHeader><DialogTitle className="text-lg font-bold">{editingId ? "Editar Cliente" : "Nuevo Cliente"}</DialogTitle></DialogHeader>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label className="text-sm font-medium text-gray-600">Nombre / Razón Social *</Label>
                                    <Input className="rounded-xl" value={clientForm.name} onChange={e => setClientForm({ ...clientForm, name: e.target.value })} />
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-sm font-medium text-gray-600">Email</Label>
                                    <Input className="rounded-xl" type="email" value={clientForm.email} onChange={e => setClientForm({ ...clientForm, email: e.target.value })} />
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-sm font-medium text-gray-600">Teléfono</Label>
                                    <Input className="rounded-xl" value={clientForm.phone} onChange={e => setClientForm({ ...clientForm, phone: e.target.value })} />
                                </div>
                            </div>
                            <DialogFooter><Button className="rounded-xl" onClick={handleSubmit}>{editingId ? "Actualizar" : "Guardar"}</Button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <Card className="border border-gray-100/80 shadow-sm">
                <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
                        <SearchBar value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar clientes..." />
                        <Button variant="outline" size="sm" className="rounded-xl w-full sm:w-auto"><Filter className="mr-2 h-4 w-4" /> Filtros</Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? <div className="flex justify-center p-8"><div className="w-8 h-8 border-3 border-legal-blue border-t-transparent rounded-full animate-spin" /></div> :
                        <div className="w-full overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-gray-100">
                                        <TableHead className="text-gray-500 font-medium">Nombre / Razón Social</TableHead>
                                        <TableHead className="text-gray-500 font-medium">Email</TableHead>
                                        <TableHead className="text-gray-500 font-medium">Teléfono</TableHead>
                                        <TableHead className="text-gray-500 font-medium">Estado</TableHead>
                                        <TableHead className="text-right text-gray-500 font-medium">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredClients.map((client) => (
                                        <TableRow
                                            key={client.id}
                                            className="cursor-pointer hover:bg-blue-50/30 transition-colors border-gray-100"
                                            onClick={() => navigate(client.id)}
                                        >
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                                                        {client.name?.substring(0, 2).toUpperCase()}
                                                    </div>
                                                    {client.name}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-gray-500">{client.email}</TableCell>
                                            <TableCell className="text-gray-500">{client.phone}</TableCell>
                                            <TableCell>
                                                <Badge className={client.status === "active" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-gray-50 text-gray-500 border border-gray-200"}>{client.status === "active" ? "Activo" : "Inactivo"}</Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="icon" className="rounded-xl hover:bg-blue-50" onClick={(e) => { e.stopPropagation(); handleEdit(client); }}><Edit2 className="h-4 w-4 text-gray-400" /></Button>
                                                <Button variant="ghost" size="icon" className="rounded-xl hover:bg-red-50" onClick={(e) => { e.stopPropagation(); deleteClient(client.id); }}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredClients.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-12 text-gray-400">No se encontraron clientes</TableCell></TableRow>}
                                </TableBody>
                            </Table>
                        </div>
                    }
                </CardContent>
            </Card>
        </div>
    );
};

// --- Procesos Page ---

const renderTitleInRows = (title: string, toast?: any) => {
    const titleStr = typeof title === 'object' ? JSON.stringify(title) : String(title || "");
    const regex = /(Demandante\/accionante|Demandado\/indiciado\/causante|Demandante\/accionante:|Demandado\/indiciado\/causante:|Demandante:|Demandado:|Accionante:|Accionado:|Ejecutante:|Ejecutado:|Fiscalia:|Fiscalía:|Numero Interno:|Número Interno:|Demandante\b|Demandado\b|Accionante\b|Accionado\b|Ejecutante\b|Ejecutado\b|Fiscalia\b|Fiscalía\b|Fiscal\b|Numero Interno\b|Número Interno\b)/i;

    if (!regex.test(titleStr)) {
        return (
            <h3 className="font-semibold text-base text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-2 leading-snug" title={titleStr}>
                {titleStr}
            </h3>
        );
    }

    const parts = titleStr.split(regex);
    const rows: { label: string; value: string }[] = [];

    let currentLabel = "";
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (!part) continue;

        if (regex.test(part)) {
            let clean = part.trim().toUpperCase().replace(/[\s:]+$/g, "");
            if (clean.includes("DEMANDANTE") || clean.includes("ACCIONANTE") || clean.includes("EJECUTANTE")) {
                clean = "DEMANDANTE/ACCIONANTE";
            } else if (clean.includes("DEMANDADO") || clean.includes("ACCIONADO") || clean.includes("EJECUTADO") || clean.includes("INDICIADO") || clean.includes("CAUSANTE")) {
                clean = "DEMANDADO/INDICIADO/CAUSANTE";
            } else if (clean.includes("FISCAL")) {
                clean = "FISCALÍA";
            } else if (clean.includes("INTERNO")) {
                clean = "N° INTERNO";
            }
            currentLabel = clean;
        } else {
            let val = part.trim().replace(/^[\s:-]+|[\s:-]+$/g, "");
            if (val.toUpperCase().startsWith("NUMERO INTERNO")) {
                val = val.replace(/^NUMERO INTERNO\s*/i, "");
            }
            if (val) {
                rows.push({ label: currentLabel || "SUJETO", value: val });
            }
        }
    }

    if (rows.length === 0) {
        return (
            <h3 className="font-semibold text-base text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-2 leading-snug" title={titleStr}>
                {titleStr}
            </h3>
        );
    }

    return (
        <div className="space-y-2 my-2.5">
            {rows.map((row, idx) => {
                const isPlaintiff = row.label.includes("DEMANDANTE");
                const isFiscalia = row.label.includes("FISCALÍA");
                const isNumeroInterno = row.label.includes("INTERNO");

                return (
                    <div key={idx} className="flex flex-col md:flex-row md:items-center gap-2 text-sm">
                        <span className={cn(
                            "font-bold shrink-0 text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider text-center md:text-left min-w-[150px]",
                            isPlaintiff
                                ? "bg-indigo-50 text-indigo-700 border-indigo-200/60"
                                : isFiscalia
                                    ? "bg-purple-50 text-purple-700 border-purple-200/60"
                                    : isNumeroInterno
                                        ? "bg-gray-50 text-gray-600 border-gray-200/60 font-mono"
                                        : "bg-slate-100 text-slate-700 border-slate-300/60"
                        )}>
                            {row.label}
                        </span>
                        <span
                            className="text-gray-900 font-semibold select-all truncate animate-none"
                            title={row.value}
                        >
                            {row.value}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const getWatchStatusBadge = (status: string) => {
    const s = (status || "").toLowerCase();
    switch (s) {
        case "updated":
            return (
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200/60 flex items-center gap-1.5 py-0.5 px-2 font-medium text-[11px] rounded-md shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Actualizado
                </Badge>
            );
        case "active":
            return (
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200/60 flex items-center gap-1.5 py-0.5 px-2 font-medium text-[11px] rounded-md shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Activo
                </Badge>
            );
        case "pending":
            return (
                <Badge className="bg-blue-50 text-blue-700 border border-blue-200/60 flex items-center gap-1.5 py-0.5 px-2 font-medium text-[11px] rounded-md shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                    Pendiente
                </Badge>
            );
        case "paused":
            return (
                <Badge className="bg-amber-50 text-amber-700 border border-amber-200/60 flex items-center gap-1.5 py-0.5 px-2 font-medium text-[11px] rounded-md shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Pausado
                </Badge>
            );
        case "archived":
            return (
                <Badge className="bg-gray-50 text-gray-600 border border-gray-200/60 flex items-center gap-1.5 py-0.5 px-2 font-medium text-[11px] rounded-md shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Archivado
                </Badge>
            );
        case "error":
            return (
                <Badge className="bg-rose-50 text-rose-700 border border-rose-200/60 flex items-center gap-1.5 py-0.5 px-2 font-medium text-[11px] rounded-md shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping" />
                    Error
                </Badge>
            );
        default:
            return (
                <Badge className="bg-slate-50 text-slate-600 border border-slate-200/60 py-0.5 px-2 font-medium text-[11px] rounded-md shadow-sm">
                    {status}
                </Badge>
            );
    }
};

// Funciones auxiliares para la agrupación de procesos
const getProcessTipo = (item: any): string => {
    const raw = [
        item.tipo_proceso,
        item.tipoProceso,
        item.case_type,
        item.naturaleza,
        item.asunto,
        item.title,
        item.clase
    ].filter(Boolean).join(" ").toLowerCase();

    if (raw.includes("penal")) return "Penal";
    if (raw.includes("laboral") || raw.includes("trabajo")) return "Laboral";
    if (raw.includes("tutela") || raw.includes("constitucional")) return "Tutelas / Constitucional";
    if (raw.includes("administrativo") || raw.includes("contencioso")) return "Administrativo";
    if (raw.includes("familia") || raw.includes("alimentos") || raw.includes("divorcio")) return "Familia";
    if (raw.includes("comercial") || raw.includes("mercantil") || raw.includes("sociedades")) return "Comercial / Mercantil";
    if (raw.includes("civil") || raw.includes("declarativo") || raw.includes("ejecutivo")) return "Civil";
    if (raw.includes("disciplinario")) return "Disciplinario";

    if (item.tipo_proceso && String(item.tipo_proceso).trim()) {
        return String(item.tipo_proceso).trim();
    }

    return "General / Sin Clasificar";
};

const getProcessInstancia = (item: any): string => {
    const raw = [
        item.instancia,
        item.recurso,
        item.sala_conoce,
        item.despacho,
        item.juzgado,
        item.ponente,
        item.asunto
    ].filter(Boolean).join(" ").toLowerCase();

    if (raw.includes("corte suprema") || raw.includes("consejo de estado") || raw.includes("corte constitucional") || raw.includes("casacion")) {
        return "Altas Cortes / Casación";
    }
    if (raw.includes("tribunal") || raw.includes("segunda") || raw.includes("apelacion") || raw.includes("2a") || raw.includes("2da")) {
        return "Segunda Instancia (Tribunales)";
    }
    if (raw.includes("juzgado") || raw.includes("primera") || raw.includes("1a") || raw.includes("1ra") || raw.includes("municipal") || raw.includes("circuito")) {
        return "Primera Instancia (Juzgados)";
    }
    if (raw.includes("arbitral") || raw.includes("centro de arbitraje")) {
        return "Tribunal Arbitral / Conciliación";
    }

    if (item.instancia && String(item.instancia).trim()) {
        return String(item.instancia).trim();
    }

    return "Primera Instancia / General";
};

const getProcessClase = (item: any): string => {
    const rawClase = String(item.clase || item.subclase || item.clase_proceso || "").trim();
    if (rawClase) return rawClase;

    const text = [item.asunto, item.title, item.description].filter(Boolean).join(" ").toLowerCase();
    if (text.includes("ejecutivo")) return "Proceso Ejecutivo";
    if (text.includes("ordinario")) return "Proceso Ordinario";
    if (text.includes("verbal sumario")) return "Verbal Sumario";
    if (text.includes("verbal")) return "Proceso Verbal";
    if (text.includes("tutela")) return "Acción de Tutela";
    if (text.includes("liquidac")) return "Liquidación / Insolvencia";
    if (text.includes("restituc")) return "Restitución de Inmueble";

    return "Clase Ordinaria / Sin Especificar";
};

const getProcessEstado = (item: any): string => {
    const status = String(item.watch_status || item.status || "").toLowerCase();
    const isWatched = item.is_watched !== false;
    const changeCount = item.change_count_last_run || 0;

    if (status === "paused" || !isWatched) return "Pausados / Inactivos";
    if (status === "error") return "Con Error de Extracción";
    if (changeCount > 0) return "Con Novedades Recientes";
    if (status === "updated" || status === "active") return "Activos / En Monitoreo";
    if (status === "archived") return "Archivados";

    return "En Seguimiento Normal";
};

const getProcessCliente = (item: any): string => {
    const client = item.client_name || item.cliente || item.client || "";
    if (client && String(client).trim()) return String(client).trim();
    return "Sin Cliente Asignado";
};

// --- Bulk Ingestion Modal Component ---

import { BulkIngestionModal } from "../components/BulkIngestionModal";
export { BulkIngestionModal };

export const ProcesosPage = ({ samaiMode = false }: { samaiMode?: boolean }) => {
    const { processes, loading, addProcess, updateProcess, archiveProcess, deleteProcess, clients, refetch } = useDespachos();
    const navigate = useNavigate();
    const { user, profile } = useAuth();
    const isAdmin =
        profile?.role === "admin" ||
        (profile as any)?.role === "tenant_owner" ||
        user?.email?.includes("admin") ||
        user?.user_metadata?.role === "admin";
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedTagFilter, setSelectedTagFilter] = useState<string>("all");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newProcess, setNewProcess] = useState({ title: "", case_number: "", description: "", status: "active", client_name: "" });
    const { toast } = useToast();

    const [favoriteProcessIds, setFavoriteProcessIds] = useState<string[]>(() => {
        try {
            return JSON.parse(localStorage.getItem("favorite_processes") || "[]");
        } catch (e) {
            return [];
        }
    });

    const handleToggleFavorite = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setFavoriteProcessIds(prev => {
            const updated = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
            localStorage.setItem("favorite_processes", JSON.stringify(updated));
            return updated;
        });
        toast({
            title: "Favoritos",
            description: "Estado de favorito actualizado correctamente.",
        });
    };

    // Scrapper integration states
    const [scrapperProcesos, setScrapperProcesos] = useState<any[]>([]);
    const [scrapperLoading, setScrapperLoading] = useState(false);
    const [newRadicado, setNewRadicado] = useState("");
    const [isWatching, setIsWatching] = useState(false);
    const [syncingProcessIds, setSyncingProcessIds] = useState<Set<string>>(new Set());
    const [watchStatusFilter, setWatchStatusFilter] = useState<"all" | "updated" | "pending" | "paused" | "error">("all");
    const [changesFilter, setChangesFilter] = useState<"all" | "with_changes" | "no_changes">("all");
    const [selectedWatchIds, setSelectedWatchIds] = useState<Set<string>>(new Set());
    const [isBulkRunning, setIsBulkRunning] = useState(false);
    const [expandedChangesId, setExpandedChangesId] = useState<string | null>(null);
    const [watchNotifications, setWatchNotifications] = useState<Record<string, any[]>>({});
    const [loadingNotificationsFor, setLoadingNotificationsFor] = useState<string | null>(null);
    const location = useLocation();
    const initialTab = location.state?.tab || (samaiMode ? "vigilancia" : "todos");
    const [activeProcessTab, setActiveProcessTab] = useState(initialTab);
    const [savedVigilanciaPreset, setSavedVigilanciaPreset] = useState("default");
    const [refreshCountdownSec, setRefreshCountdownSec] = useState(120);
    const [vigilanciaRunStatus, setVigilanciaRunStatus] = useState<any>(null);
    const [isTriggeringRunNow, setIsTriggeringRunNow] = useState(false);

    // Pagination states for "Todos" and "Vigilancia Judicial"
    const [pageSizeTodos, setPageSizeTodos] = useState<number>(10);
    const [currentPageTodos, setCurrentPageTodos] = useState<number>(1);

    const [pageSizeVigilancia, setPageSizeVigilancia] = useState<number>(10);
    const [currentPageVigilancia, setCurrentPageVigilancia] = useState<number>(1);

    // Compute all unique tags available across processes for filtering
    const availableTags = useMemo(() => {
        const set = new Set<string>();
        processes.forEach(p => {
            (p.tags || []).forEach(t => {
                if (t && typeof t === "string" && t.trim()) set.add(t.trim());
            });
        });
        // Include common presets if no tags exist yet
        ["Urgente", "Importante", "Civil", "Laboral", "Penal", "Cobro PH"].forEach(t => set.add(t));
        return Array.from(set);
    }, [processes]);

    const tagCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        processes.forEach(p => {
            (p.tags || []).forEach(t => {
                if (t && typeof t === "string") {
                    const trimmed = t.trim();
                    counts[trimmed] = (counts[trimmed] || 0) + 1;
                }
            });
        });
        return counts;
    }, [processes]);

    const filteredProcesses = useMemo(() => {
        const matches = processes.filter(p => {
            const title = typeof p.title === 'object' ? JSON.stringify(p.title) : String(p.title || "");
            const caseNumber = typeof p.case_number === 'object' ? JSON.stringify(p.case_number) : String(p.case_number || "");
            const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                caseNumber.toLowerCase().includes(searchTerm.toLowerCase());
            if (!matchesSearch) return false;

            if (selectedTagFilter !== "all") {
                const pTags = (p.tags || []).map(t => typeof t === "string" ? t.trim().toLowerCase() : "");
                if (!pTags.includes(selectedTagFilter.toLowerCase())) return false;
            }
            return true;
        });

        return [...matches].sort((a, b) => {
            const aFav = favoriteProcessIds.includes(a.id) || favoriteProcessIds.includes(a.case_number);
            const bFav = favoriteProcessIds.includes(b.id) || favoriteProcessIds.includes(b.case_number);
            if (aFav && !bFav) return -1;
            if (!aFav && bFav) return 1;
            return 0;
        });
    }, [processes, searchTerm, selectedTagFilter, favoriteProcessIds]);

    const vigilanciaStats = useMemo(() => {
        const total = scrapperProcesos.length;
        const updated = scrapperProcesos.filter(p => (p.watch_status || "").toLowerCase() === "updated").length;
        const paused = scrapperProcesos.filter(p => (p.watch_status || "").toLowerCase() === "paused" || p.is_watched === false).length;
        const errors = scrapperProcesos.filter(p => (p.watch_status || "").toLowerCase() === "error").length;
        const withChanges = scrapperProcesos.filter(p => (p.change_count_last_run || 0) > 0).length;
        return { total, updated, paused, errors, withChanges };
    }, [scrapperProcesos]);

    const filteredScrapperProcesos = useMemo(() => {
        const matches = scrapperProcesos.filter(p => {
            const matchesSearch =
                (p.numero_proceso || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (p.asunto || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (p.ponente || "").toLowerCase().includes(searchTerm.toLowerCase());

            if (!matchesSearch) return false;

            const status = (p.watch_status || "").toLowerCase();
            const matchesStatus =
                watchStatusFilter === "all"
                    ? true
                    : watchStatusFilter === "paused"
                        ? status === "paused" || p.is_watched === false
                        : status === watchStatusFilter;
            if (!matchesStatus) return false;

            const changeCount = p.change_count_last_run || 0;
            const matchesChanges =
                changesFilter === "all" ||
                (changesFilter === "with_changes" && changeCount > 0) ||
                (changesFilter === "no_changes" && changeCount === 0);
            if (!matchesChanges) return false;

            if (selectedTagFilter !== "all") {
                const linkedProc = processes.find(
                    (proc) =>
                        proc.case_number === p.numero_proceso ||
                        (proc.case_number && p.numero_proceso && proc.case_number.replace(/\D/g, "") === p.numero_proceso.replace(/\D/g, ""))
                );
                const pTags = ((linkedProc?.tags) || (p.metadata?.tags) || []).map((t: any) =>
                    typeof t === "string" ? t.trim().toLowerCase() : ""
                );
                if (!pTags.includes(selectedTagFilter.toLowerCase())) return false;
            }

            return true;
        });

        return [...matches].sort((a, b) => {
            const aFav = favoriteProcessIds.includes(a.id) || favoriteProcessIds.includes(a.numero_proceso);
            const bFav = favoriteProcessIds.includes(b.id) || favoriteProcessIds.includes(b.numero_proceso);
            if (aFav && !bFav) return -1;
            if (!aFav && bFav) return 1;
            return 0;
        });
    }, [scrapperProcesos, searchTerm, watchStatusFilter, changesFilter, selectedTagFilter, processes, favoriteProcessIds]);

    // Reset pagination when search or filters change
    useEffect(() => {
        setCurrentPageTodos(1);
        setCurrentPageVigilancia(1);
    }, [searchTerm, watchStatusFilter, changesFilter, selectedTagFilter]);

    // Grouping states & memoizations
    const [groupByTodos, setGroupByTodos] = useState<"none" | "tipo" | "instancia" | "clase" | "cliente">("none");
    const [groupByVigilancia, setGroupByVigilancia] = useState<"none" | "tipo" | "instancia" | "clase" | "estado">("none");
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

    const toggleGroup = (groupKey: string) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: prev[groupKey] === undefined ? false : !prev[groupKey]
        }));
    };

    const groupedProcessesTodos = useMemo(() => {
        if (groupByTodos === "none") return null;

        const groups: Record<string, any[]> = {};

        filteredProcesses.forEach(proc => {
            let key = "General";
            if (groupByTodos === "tipo") key = getProcessTipo(proc);
            else if (groupByTodos === "instancia") key = getProcessInstancia(proc);
            else if (groupByTodos === "clase") key = getProcessClase(proc);
            else if (groupByTodos === "cliente") key = getProcessCliente(proc);

            if (!groups[key]) groups[key] = [];
            groups[key].push(proc);
        });

        return groups;
    }, [filteredProcesses, groupByTodos]);

    const groupedScrapperProcesos = useMemo(() => {
        if (groupByVigilancia === "none") return null;

        const groups: Record<string, any[]> = {};

        filteredScrapperProcesos.forEach(proc => {
            let key = "General";
            if (groupByVigilancia === "tipo") key = getProcessTipo(proc);
            else if (groupByVigilancia === "instancia") key = getProcessInstancia(proc);
            else if (groupByVigilancia === "clase") key = getProcessClase(proc);
            else if (groupByVigilancia === "estado") key = getProcessEstado(proc);

            if (!groups[key]) groups[key] = [];
            groups[key].push(proc);
        });

        return groups;
    }, [filteredScrapperProcesos, groupByVigilancia]);

    const paginatedProcesses = useMemo(() => {
        const start = (currentPageTodos - 1) * pageSizeTodos;
        return filteredProcesses.slice(start, start + pageSizeTodos);
    }, [filteredProcesses, currentPageTodos, pageSizeTodos]);

    const totalPagesTodos = useMemo(() => {
        return Math.ceil(filteredProcesses.length / pageSizeTodos) || 1;
    }, [filteredProcesses.length, pageSizeTodos]);

    const paginatedScrapperProcesos = useMemo(() => {
        const start = (currentPageVigilancia - 1) * pageSizeVigilancia;
        return filteredScrapperProcesos.slice(start, start + pageSizeVigilancia);
    }, [filteredScrapperProcesos, currentPageVigilancia, pageSizeVigilancia]);

    const totalPagesVigilancia = useMemo(() => {
        return Math.ceil(filteredScrapperProcesos.length / pageSizeVigilancia) || 1;
    }, [filteredScrapperProcesos.length, pageSizeVigilancia]);

    const renderPaginationControls = (
        currentPage: number,
        totalPages: number,
        totalItems: number,
        pageSize: number,
        onPageChange: (page: number) => void,
        onPageSizeChange: (size: number) => void
    ) => {
        if (totalItems === 0) return null;

        const startItem = (currentPage - 1) * pageSize + 1;
        const endItem = Math.min(currentPage * pageSize, totalItems);

        const getPageNumbers = () => {
            const pages: (number | string)[] = [];
            if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                if (currentPage > 3) pages.push("...");
                const start = Math.max(2, currentPage - 1);
                const end = Math.min(totalPages - 1, currentPage + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                if (currentPage < totalPages - 2) pages.push("...");
                pages.push(totalPages);
            }
            return pages;
        };

        return (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-3 px-2 border-t border-gray-100 mt-4 text-xs text-gray-500 bg-gray-50/50 rounded-xl">
                <div className="flex items-center gap-2.5">
                    <span className="font-semibold text-gray-600">Registros por página:</span>
                    <Select
                        value={String(pageSize)}
                        onValueChange={(val) => {
                            onPageSizeChange(Number(val));
                            onPageChange(1);
                        }}
                    >
                        <SelectTrigger className="h-8 w-[80px] rounded-lg text-xs font-semibold bg-white border-gray-200 shadow-sm">
                            <SelectValue placeholder={String(pageSize)} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="30">30</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                    </Select>
                    <span className="text-gray-500 font-medium">
                        (Mostrando <strong className="text-gray-700">{startItem}-{endItem}</strong> de <strong className="text-gray-700">{totalItems}</strong>)
                    </span>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center gap-1.5">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-3 rounded-lg text-xs font-medium bg-white"
                            disabled={currentPage <= 1}
                            onClick={() => onPageChange(currentPage - 1)}
                        >
                            Anterior
                        </Button>

                        {getPageNumbers().map((p, idx) =>
                            typeof p === "number" ? (
                                <Button
                                    key={p}
                                    variant={p === currentPage ? "default" : "ghost"}
                                    size="sm"
                                    className={cn(
                                        "h-8 w-8 p-0 rounded-lg text-xs font-bold transition-colors",
                                        p === currentPage
                                            ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                                            : "text-gray-600 hover:bg-gray-200/60"
                                    )}
                                    onClick={() => onPageChange(p)}
                                >
                                    {p}
                                </Button>
                            ) : (
                                <span key={`ellipsis-${idx}`} className="px-1 text-gray-400 font-bold">
                                    {p}
                                </span>
                            )
                        )}

                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-3 rounded-lg text-xs font-medium bg-white"
                            disabled={currentPage >= totalPages}
                            onClick={() => onPageChange(currentPage + 1)}
                        >
                            Siguiente
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const renderProcessCard = (process: any, index: number) => {
        return (
            <Card
                key={process.id}
                className={`group border border-gray-100/85 hover:border-indigo-150 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer overflow-hidden rounded-xl bg-white shadow-sm animate-fade-in-up stagger-${(index % 11) + 1}`}
                onClick={() => navigate(process.id)}
            >
                <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row md:items-stretch min-h-[140px]">
                        {/* Left Accent line based on status */}
                        <div className={cn(
                            "w-1 md:w-1.5 transition-all group-hover:w-2 shrink-0",
                            process.status === "active" || (process.status || "").toLowerCase() === "updated"
                                ? "bg-emerald-500"
                                : process.status === "archived" || (process.status || "").toLowerCase() === "paused"
                                    ? "bg-amber-500"
                                    : (process.status || "").toLowerCase() === "error"
                                        ? "bg-rose-500"
                                        : "bg-blue-500"
                        )} />

                        <div className="flex-1 p-5 flex flex-col md:flex-row justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                {/* Top Line: Badges */}
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    {getWatchStatusBadge(process.status)}

                                    {process.tipo_proceso && (
                                        <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200/60 font-medium text-[10px] py-0.5 px-2">
                                            {process.tipo_proceso}
                                        </Badge>
                                    )}
                                    {process.clase && (
                                        <Badge variant="outline" className="bg-indigo-50/30 text-indigo-700 border-indigo-100/50 font-medium text-[10px] py-0.5 px-2">
                                            {process.clase}
                                        </Badge>
                                    )}
                                    {process.client_name && (
                                        <Badge variant="secondary" className="bg-gray-100/80 text-gray-800 font-semibold text-[10px] py-0.5 px-2">
                                            Cliente: {process.client_name}
                                        </Badge>
                                    )}

                                    {/* Etiquetas / Tags del expediente */}
                                    {process.tags && process.tags.length > 0 && (
                                        process.tags.slice(0, 3).map((tag: string) => {
                                            const isUrgent = tag.toLowerCase() === "urgente";
                                            const isImportant = tag.toLowerCase() === "importante";
                                            return (
                                                <Badge
                                                    key={tag}
                                                    variant="outline"
                                                    className={cn(
                                                        "text-[10px] py-0.5 px-2 font-medium rounded-md flex items-center gap-1",
                                                        isUrgent
                                                            ? "bg-amber-50 text-amber-700 border-amber-200"
                                                            : isImportant
                                                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                                                : "bg-slate-100/80 text-slate-700 border-slate-200"
                                                    )}
                                                >
                                                    <Tag className="h-2.5 w-2.5 opacity-60" />
                                                    {tag}
                                                </Badge>
                                            );
                                        })
                                    )}
                                    {process.tags && process.tags.length > 3 && (
                                        <Badge variant="outline" className="text-[9px] py-0.5 px-1.5 text-gray-500 bg-gray-50 border-gray-200 font-medium">
                                            +{process.tags.length - 3}
                                        </Badge>
                                    )}
                                </div>

                                {/* Case Title (Parsed in Row Style if needed) */}
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        {renderTitleInRows(process.title, toast)}
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        title={favoriteProcessIds.includes(process.id) || favoriteProcessIds.includes(process.case_number) ? "Quitar de favoritos" : "Marcar como favorito"}
                                        className={cn(
                                            "rounded-full h-8 w-8 p-0 shrink-0 transition-colors",
                                            favoriteProcessIds.includes(process.id) || favoriteProcessIds.includes(process.case_number)
                                                ? "text-amber-500 hover:text-amber-650 hover:bg-amber-50"
                                                : "text-gray-300 hover:text-amber-400 hover:bg-gray-50"
                                        )}
                                        onClick={(e) => handleToggleFavorite(process.id || process.case_number, e)}
                                    >
                                        <Star className={cn("h-4 w-4", (favoriteProcessIds.includes(process.id) || favoriteProcessIds.includes(process.case_number)) ? "fill-amber-500" : "")} />
                                    </Button>
                                </div>

                                {/* Expediente / Radicado with Copy capability */}
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Expediente:</span>
                                    <code
                                        className="font-mono text-xs text-indigo-600 font-bold bg-indigo-50/40 px-2 py-0.5 rounded border border-indigo-100/40 select-all hover:bg-indigo-50 transition-colors cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigator.clipboard.writeText(process.case_number);
                                            toast({
                                                title: "Copiado",
                                                description: "Número de expediente copiado al portapapeles.",
                                            });
                                        }}
                                        title="Haga clic para copiar expediente"
                                    >
                                        {process.case_number}
                                    </code>
                                </div>

                                {/* Judicial Metadata Grid: Judge, Court */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 mt-3 pt-3 border-t border-gray-100/80">
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <Scale className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                        <span className="truncate" title={process.juzgado || "Juzgado no especificado"}>
                                            <strong>Juzgado:</strong> {process.juzgado || <span className="text-gray-300 italic">No especificado</span>}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                        <span className="truncate" title={process.ponente || "Ponente no especificado"}>
                                            <strong>Ponente:</strong> {process.ponente || <span className="text-gray-300 italic">No especificado</span>}
                                        </span>
                                    </div>
                                </div>

                                {/* Description if present */}
                                {process.description && (
                                    <p className="text-xs text-gray-500 mt-2 line-clamp-1 italic">
                                        {process.description}
                                    </p>
                                )}
                            </div>

                            {/* Right Column: Time Stamps and Action Buttons */}
                            <div className="flex flex-row md:flex-col justify-between items-end md:text-right text-sm text-gray-500 shrink-0 min-w-[130px] md:border-l md:border-gray-100/80 md:pl-4">
                                {/* Dates */}
                                <div className="flex flex-col gap-1 text-[11px] text-gray-400 font-medium">
                                    <span className="flex items-center justify-end gap-1.5">
                                        <Clock className="h-3 w-3" />
                                        Act: {process.updated_at ? new Date(process.updated_at).toLocaleDateString() : 'N/D'}
                                    </span>
                                    {process.last_change_at && (
                                        <span className="flex items-center justify-end gap-1.5 text-indigo-500/80 font-semibold">
                                            <Zap className="h-3 w-3 text-indigo-400 shrink-0" />
                                            Cambio: {new Date(process.last_change_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-1.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-250 mt-2 md:mt-0">
                                    {process.status !== "archived" && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            title="Archivar proceso"
                                            className="text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg h-8 w-8 p-0 transition-colors"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleArchiveInternalProcess(process.id, process.title);
                                            }}
                                        >
                                            <Box className="h-4 w-4" />
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        title={process.status === "archived" ? "Eliminar proceso" : "Primero archiva el proceso para poder eliminarlo"}
                                        disabled={process.status !== "archived"}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg h-8 w-8 p-0 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteInternalProcess(process.id, process.title);
                                        }}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    const renderScrapperProcessCard = (p: any, index: number) => {
        const isSyncing = syncingProcessIds.has(p.numero_proceso);
        const selected = selectedWatchIds.has(p.numero_proceso);
        const isPaused = (p.watch_status || "").toLowerCase() === "paused" || p.is_watched === false;
        const rawStatus = (p.watch_status || "pending").toLowerCase();
        const hasMetadata = Boolean(p.last_check_at || p.fecha_ultima_revision || p.tipo_proceso || p.clase || p.ponente || p.asunto);
        const isPendingOrChecking = isSyncing || rawStatus === "checking" || (rawStatus === "pending" && !hasMetadata);

        let statusText = "Vigilado";
        let statusClass = "bg-emerald-50 text-emerald-700 border-emerald-200";

        if (isPaused) {
            statusText = "Pausado";
            statusClass = "bg-amber-50 text-amber-700 border-amber-200";
        } else if (isSyncing || rawStatus === "checking") {
            statusText = "Extrayendo datos...";
            statusClass = "bg-indigo-50 text-indigo-700 border-indigo-200 animate-pulse";
        } else if (rawStatus === "updated") {
            statusText = "Actualizado";
            statusClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
        } else if (rawStatus === "pending" && !hasMetadata) {
            statusText = "En cola de extracción";
            statusClass = "bg-blue-50 text-blue-700 border-blue-200 animate-pulse";
        } else if (rawStatus === "error") {
            statusText = "Error de consulta";
            statusClass = "bg-rose-50 text-rose-700 border-rose-200";
        }

        const vigenteInfo = getVigenteStatusInfo(p.vigente);
        let vigenteText = vigenteInfo.text;
        let vigenteClass = vigenteInfo.badgeClass;

        if (!p.vigente) {
            if (isPendingOrChecking) {
                vigenteText = "Consultando...";
                vigenteClass = "bg-blue-50 text-blue-600 border border-blue-200 animate-pulse";
            }
        }

        const linkedProc = processes.find(
            (item) => item.case_number === p.numero_proceso || item.radicado === p.numero_proceso
        );
        const cardTags: string[] = (linkedProc?.tags) || (p.metadata?.tags) || [];

        return (
            <Card
                key={p.numero_proceso}
                className={`border border-gray-100/80 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group animate-fade-in-up stagger-${(index % 11) + 1}`}
                onClick={() => navigate(`/dashboard/despachos/procesos/${p.numero_proceso}`)}
            >
                <CardContent className="p-5">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-4 w-full">
                        <div className="flex items-start gap-3 flex-1 min-w-0 w-full md:w-auto">
                            <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                                <Checkbox checked={selected} onCheckedChange={(v: any) => handleToggleSelection(p.numero_proceso, Boolean(v))} />
                            </div>
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md shrink-0">
                                {isSyncing || rawStatus === "checking" ? <RefreshCw className="h-5 w-5 text-white animate-spin" /> : <Scale className="h-5 w-5 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <h3 className="font-mono font-bold text-sm text-indigo-600 truncate max-w-full">{p.numero_proceso}</h3>
                                    <Badge className={`text-[10px] ${vigenteClass}`}>
                                        {vigenteText}
                                    </Badge>
                                    <Badge className={`text-[10px] border ${statusClass}`}>
                                        {statusText}
                                    </Badge>

                                    {/* Process Tags */}
                                    {cardTags.slice(0, 3).map((tag: string) => {
                                        const isUrgent = tag.toLowerCase() === "urgente";
                                        const isImportant = tag.toLowerCase() === "importante";
                                        return (
                                            <Badge
                                                key={tag}
                                                variant="outline"
                                                className={cn(
                                                    "text-[10px] py-0.5 px-2 font-medium rounded-md flex items-center gap-1",
                                                    isUrgent
                                                        ? "bg-amber-50 text-amber-700 border-amber-200"
                                                        : isImportant
                                                            ? "bg-blue-50 text-blue-700 border-blue-200"
                                                            : "bg-slate-100/80 text-slate-700 border-slate-200"
                                                )}
                                            >
                                                <Tag className="h-2.5 w-2.5 opacity-60" />
                                                {tag}
                                            </Badge>
                                        );
                                    })}
                                    {cardTags.length > 3 && (
                                        <Badge variant="outline" className="text-[9px] py-0.5 px-1.5 text-gray-500 bg-gray-50 border-gray-200 font-medium">
                                            +{cardTags.length - 3}
                                        </Badge>
                                    )}
                                </div>
                                {p.tipo_proceso || p.clase ? (
                                    <p className="text-sm text-gray-800 font-medium line-clamp-1">{p.tipo_proceso || p.clase}</p>
                                ) : isPendingOrChecking ? (
                                    <p className="text-sm text-indigo-600 font-semibold flex items-center gap-1.5 animate-pulse">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500 shrink-0" />
                                        Extrayendo datos del proceso en tiempo real...
                                    </p>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">Proceso registrado (Pendiente de clasificación)</p>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-400">
                                    {p.ponente && <span>Ponente: <span className="text-gray-600">{p.ponente}</span></span>}
                                    {p.sala_conoce && <span>Sala: <span className="text-gray-600">{p.sala_conoce}</span></span>}
                                </div>
                                {p.asunto && (
                                    <p className="text-xs text-gray-400 mt-1 line-clamp-1 italic">{p.asunto}</p>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[11px] text-gray-400">
                                    <span>Último check: {p.last_check_at ? new Date(p.last_check_at).toLocaleString() : "N/D"}</span>
                                    <span>Último cambio: {p.last_change_at ? new Date(p.last_change_at).toLocaleString() : "N/D"}</span>
                                </div>
                                {isPaused && (
                                    <div className="mt-2.5 flex items-center gap-2 bg-amber-50/90 border border-amber-200/80 rounded-lg px-3 py-1.5 text-xs text-amber-800 font-medium animate-fade-in">
                                        <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                                        <span>Vigilancia pausada: Las actualizaciones automáticas de este proceso están suspendidas.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-start w-full md:w-auto border-t md:border-t-0 border-gray-100 pt-3 md:pt-0 gap-2 shrink-0">
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {p.last_check_at ? (
                                    new Date(p.last_check_at).toLocaleDateString()
                                ) : p.fecha_ultima_revision ? (
                                    new Date(p.fecha_ultima_revision).toLocaleDateString()
                                ) : isPendingOrChecking ? (
                                    <span className="text-indigo-600 font-medium flex items-center gap-1">
                                        <Loader2 className="h-3 w-3 animate-spin" /> Extrayendo...
                                    </span>
                                ) : (
                                    "N/D"
                                )}
                            </p>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn(
                                        "h-8 w-8 rounded-lg transition-colors",
                                        favoriteProcessIds.includes(p.numero_proceso)
                                            ? "text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                                            : "text-gray-300 hover:text-amber-400 hover:bg-gray-50"
                                    )}
                                    onClick={(e) => handleToggleFavorite(p.numero_proceso, e)}
                                    title={favoriteProcessIds.includes(p.numero_proceso) ? "Quitar de favoritos" : "Marcar como favorito"}
                                >
                                    <Star className={cn("h-3.5 w-3.5", favoriteProcessIds.includes(p.numero_proceso) ? "fill-amber-500" : "")} />
                                </Button>
                                {isPaused ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 px-2.5 rounded-lg border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold text-xs transition-colors"
                                        title="Reactivar la vigilancia automática de este proceso"
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                                const { setScrapperProcesoWatch } = await import("@/services/scraperApi");
                                                await setScrapperProcesoWatch(p.numero_proceso, true);
                                                setScrapperProcesos(prev =>
                                                    prev.map(item => item.numero_proceso === p.numero_proceso
                                                        ? { ...item, is_watched: true, watch_status: "active" }
                                                        : item
                                                    )
                                                );
                                                toast({
                                                    title: "Vigilancia reactivada",
                                                    description: `El radicado ${p.numero_proceso} está nuevamente activo.`,
                                                });
                                                await loadScrapperProcesos(user?.id);
                                            } catch (err) {
                                                toast({ title: "Error", description: String(err), variant: "destructive" });
                                            }
                                        }}
                                    >
                                        <Play className="h-3.5 w-3.5 mr-1 fill-emerald-600 text-emerald-600" />
                                        Reactivar
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 px-2.5 rounded-lg border-amber-200 bg-amber-50/60 text-amber-700 hover:bg-amber-100 font-semibold text-xs transition-colors"
                                        title="Pausar la vigilancia de este proceso"
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                                const { setScrapperProcesoWatch } = await import("@/services/scraperApi");
                                                await setScrapperProcesoWatch(p.numero_proceso, false);
                                                setScrapperProcesos(prev =>
                                                    prev.map(item => item.numero_proceso === p.numero_proceso
                                                        ? { ...item, is_watched: false, watch_status: "paused" }
                                                        : item
                                                    )
                                                );
                                                toast({
                                                    title: "Vigilancia pausada",
                                                    description: `El radicado ${p.numero_proceso} se ha puesto en pausa.`,
                                                });
                                                await loadScrapperProcesos(user?.id);
                                            } catch (err) {
                                                toast({ title: "Error", description: String(err), variant: "destructive" });
                                            }
                                        }}
                                    >
                                        <Pause className="h-3.5 w-3.5 mr-1 fill-amber-600 text-amber-600" />
                                        Pausar
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Dejar de vigilar (no elimina el expediente)"
                                    className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg h-8 w-8 p-0 flex items-center justify-center animate-fade-in"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteScrapper(p.numero_proceso); }}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Ver cambios recientes"
                                    className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg h-8 w-8 p-0 flex items-center justify-center animate-fade-in"
                                    onClick={(e) => { e.stopPropagation(); handleViewChanges(p.numero_proceso); }}
                                >
                                    <FileText className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    </div>
                    {expandedChangesId === p.numero_proceso && (
                        <div className="mt-4 border-t border-gray-100 pt-3">
                            {loadingNotificationsFor === p.numero_proceso ? (
                                <div className="text-xs text-gray-500">Cargando cambios...</div>
                            ) : (watchNotifications[p.numero_proceso] || []).length === 0 ? (
                                <div className="text-xs text-gray-500">No hay cambios recientes para este radicado.</div>
                            ) : (
                                <div className="space-y-2">
                                    {(watchNotifications[p.numero_proceso] || []).slice(0, 6).map((n: any, idxN: number) => (
                                        <div key={`${n.id || idxN}`} className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-2">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">{n.tipo || "update"}</span>
                                                <span className="text-[10px] text-gray-400">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</span>
                                            </div>
                                            <p className="text-xs text-gray-700">{n.message || "Sin detalle"}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    };


    // Load scrapper procesos — re-run whenever the authenticated user changes
    useEffect(() => {
        loadScrapperProcesos(user?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    useEffect(() => {
        const saved = localStorage.getItem("vigilancia_preset");
        if (saved) {
            setSavedVigilanciaPreset(saved);
            if (saved === "updated_only") {
                setWatchStatusFilter("updated");
                setChangesFilter("with_changes");
            } else if (saved === "paused_only") {
                setWatchStatusFilter("paused");
                setChangesFilter("all");
            } else if (saved === "errors_only") {
                setWatchStatusFilter("error");
                setChangesFilter("all");
            } else {
                setWatchStatusFilter("all");
                setChangesFilter("all");
            }
        }
    }, []);

    const loadScrapperProcesos = async (uid?: string) => {
        setScrapperLoading(true);
        try {
            const targetUid = uid !== undefined ? uid : user?.id;
            const { fetchScrapperProcesos, fetchScrapperProceso, fetchVigilanciaCronStatus } = await import("@/services/scraperApi");
            const data = await fetchScrapperProcesos(targetUid);
            const base = data || [];
            setScrapperProcesos(base);
            const cronStatus = await fetchVigilanciaCronStatus().catch(() => ({}));
            setVigilanciaRunStatus(cronStatus || {});

            // Reliability fix: some list payloads come partial; hydrate missing cards from detail endpoint.
            const missing = base.filter((p: any) =>
                !(p?.asunto || p?.tipo_proceso || p?.clase || p?.ponente || p?.sala_conoce)
            ).slice(0, 20);

            if (missing.length > 0) {
                const hydrated = await Promise.allSettled(
                    missing.map((p: any) => fetchScrapperProceso(p.numero_proceso))
                );
                const detailMap = new Map<string, any>();
                hydrated.forEach((r) => {
                    if (r.status === "fulfilled" && r.value?.numero_proceso) {
                        detailMap.set(r.value.numero_proceso, r.value);
                    }
                });
                if (detailMap.size > 0) {
                    setScrapperProcesos((prev: any[]) =>
                        prev.map((p: any) => detailMap.has(p.numero_proceso) ? { ...p, ...detailMap.get(p.numero_proceso) } : p)
                    );
                }
            }
        } catch (err) {
            console.warn("Scrapper API not available:", err);
        }
        setScrapperLoading(false);
    };

    useEffect(() => {
        if (activeProcessTab !== "vigilancia") return;
        setRefreshCountdownSec(120);
        const uid = user?.id;
        const t = setInterval(() => {
            setRefreshCountdownSec((prev) => {
                if (prev <= 1) {
                    loadScrapperProcesos(uid);
                    return 120;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(t);
    }, [activeProcessTab, user?.id]);

    const handleApplyPreset = (preset: string) => {
        setSavedVigilanciaPreset(preset);
        localStorage.setItem("vigilancia_preset", preset);
        if (preset === "updated_only") {
            setWatchStatusFilter("updated");
            setChangesFilter("with_changes");
        } else if (preset === "paused_only") {
            setWatchStatusFilter("paused");
            setChangesFilter("all");
        } else if (preset === "errors_only") {
            setWatchStatusFilter("error");
            setChangesFilter("all");
        } else {
            setWatchStatusFilter("all");
            setChangesFilter("all");
        }
    };

    const handleRunVigilanciaNow = async () => {
        setIsTriggeringRunNow(true);
        try {
            const { runVigilanciaNow } = await import("@/services/scraperApi");
            const res = await runVigilanciaNow();
            toast({
                title: "Vigilancia encolada",
                description: res?.message || "Se disparó el seguimiento inmediato de procesos vigilados.",
            });
            setTimeout(() => loadScrapperProcesos(user?.id), 1500);
        } catch (err) {
            toast({ title: "Error run now", description: String(err), variant: "destructive" });
        } finally {
            setIsTriggeringRunNow(false);
        }
    };

    const cronChip = useMemo(() => {
        const s = (vigilanciaRunStatus?.status || "").toLowerCase();
        if (s === "running") {
            return {
                label: "Cron: ejecutando",
                cls: "bg-indigo-50 text-indigo-700 border-indigo-200",
            };
        }
        if (s === "success") {
            return {
                label: "Cron: OK",
                cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
            };
        }
        if (s === "error") {
            return {
                label: "Cron: error",
                cls: "bg-rose-50 text-rose-700 border-rose-200",
            };
        }
        return {
            label: "Cron: no iniciado",
            cls: "bg-slate-50 text-slate-600 border-slate-200",
        };
    }, [vigilanciaRunStatus]);

    const handleCreate = async () => {
        if (!newProcess.title || !newProcess.case_number) {
            toast({ title: "Error", description: "Título y Número de Expediente son obligatorios", variant: "destructive" });
            return;
        }
        await addProcess({
            ...newProcess,
            updated_at: new Date().toISOString()
        });
        setIsDialogOpen(false);
        setNewProcess({ title: "", case_number: "", description: "", status: "active", client_name: "" });
    }

    const handleDeleteInternalProcess = async (processId: string, title?: string) => {
        if (!window.confirm(`¿Eliminar el proceso "${title || processId}" del listado interno?`)) {
            return;
        }
        await deleteProcess(processId);
    };

    const handleArchiveInternalProcess = async (processId: string, title?: string) => {
        if (!window.confirm(`¿Archivar el proceso "${title || processId}"?`)) {
            return;
        }
        if (typeof archiveProcess === "function") {
            await archiveProcess(processId);
            return;
        }
        await updateProcess(processId, { status: "archived" });
        toast({ title: "Proceso archivado" });
    };

    const handleWatchRadicado = async () => {
        const radicadoToWatch = newRadicado;
        if (!radicadoToWatch || radicadoToWatch.length < 20) {
            toast({ title: "Error", description: "El radicado debe tener al menos 20 dígitos", variant: "destructive" });
            return;
        }

        if (!user?.id) {
            toast({ title: "Inicio de sesión requerido", description: "Debes iniciar sesión con tu cuenta para vigilar procesos.", variant: "destructive" });
            return;
        }

        setSyncingProcessIds((prev) => new Set(prev).add(radicadoToWatch));
        toast({ title: "Iniciando vigilancia", description: `Configurando el monitoreo para el radicado ${radicadoToWatch}...` });
        setIsWatching(true);

        try {
            const { watchProceso } = await import("@/services/scraperApi");
            const watchRes = await watchProceso(radicadoToWatch, "CPNU-NACIONAL", undefined, user?.id);
            toast({
                title: "Expediente vigilado",
                description: watchRes.message || `Radicado ${radicadoToWatch} añadido al sistema de vigilancia automática.`
            });
            setNewRadicado("");
            await loadScrapperProcesos(user?.id);
        } catch (err) {
            toast({ title: "Error", description: String(err), variant: "destructive" });
        } finally {
            setIsWatching(false);
            setSyncingProcessIds(prev => {
                const updated = new Set(prev);
                updated.delete(radicadoToWatch);
                return updated;
            });
        }
    };

    const handleDeleteScrapper = async (radicado: string) => {
        if (!window.confirm(`¿Dejar de vigilar el radicado ${radicado}? El expediente permanece en base de datos; solo se desactiva la vigilancia automática.`)) {
            return;
        }
        try {
            const { deleteScrapperProceso } = await import("@/services/scraperApi");
            const res = await deleteScrapperProceso(radicado);
            toast({
                title: "Vigilancia desactivada",
                description: res?.message || `Ya no se sincronizará el radicado ${radicado}.`,
            });
            setScrapperProcesos(prev => prev.filter(p => p.numero_proceso !== radicado));
        } catch (err) {
            toast({ title: "Error", description: String(err), variant: "destructive" });
        }
    };

    const handleRefreshProcess = async (radicado: string) => {
        setSyncingProcessIds(prev => new Set(prev).add(radicado));
        try {
            const currentProcess = scrapperProcesos.find(p => p.numero_proceso === radicado);
            const { watchProceso, runVigilanciaNow } = await import("@/services/scraperApi");
            await watchProceso(radicado, currentProcess?.origen || "CPNU-NACIONAL", undefined, user?.id);
            await runVigilanciaNow().catch(() => {});
            toast({
                title: "Extracción Iniciada",
                description: `Se ha ejecutado la extracción en tiempo real para el radicado ${radicado}.`
            });
            setTimeout(() => loadScrapperProcesos(user?.id), 2500);
        } catch (err) {
            toast({ title: "Error al refrescar", description: String(err), variant: "destructive" });
        } finally {
            setSyncingProcessIds(prev => {
                const updated = new Set(prev);
                updated.delete(radicado);
                return updated;
            });
        }
    };

    const handleToggleSelection = (radicado: string, checked: boolean) => {
        setSelectedWatchIds(prev => {
            const updated = new Set(prev);
            if (checked) updated.add(radicado);
            else updated.delete(radicado);
            return updated;
        });
    };

    const handleSelectAllFiltered = (checked: boolean) => {
        if (!checked) {
            setSelectedWatchIds(new Set());
            return;
        }
        setSelectedWatchIds(new Set(filteredScrapperProcesos.map(p => p.numero_proceso)));
    };

    const handleBulkAction = async (action: "pause" | "resume" | "sync") => {
        const targets = Array.from(selectedWatchIds);
        if (!targets.length) return;
        setIsBulkRunning(true);
        try {
            if (action === "sync") {
                for (const radicado of targets) {
                    await handleRefreshProcess(radicado);
                }
            } else {
                const { setScrapperProcesoWatch } = await import("@/services/scraperApi");
                const watched = action === "resume";
                for (const radicado of targets) {
                    await setScrapperProcesoWatch(radicado, watched);
                }
                setScrapperProcesos(prev =>
                    prev.map(item => targets.includes(item.numero_proceso)
                        ? { ...item, is_watched: watched, watch_status: watched ? "active" : "paused" }
                        : item
                    )
                );
                toast({
                    title: watched ? "Vigilancia reactivada" : "Vigilancia pausada",
                    description: `${targets.length} proceso(s) actualizado(s).`,
                });
                await loadScrapperProcesos(user?.id);
            }
            setSelectedWatchIds(new Set());
        } catch (err) {
            toast({ title: "Error en acción masiva", description: String(err), variant: "destructive" });
        } finally {
            setIsBulkRunning(false);
        }
    };

    const handleViewChanges = async (radicado: string) => {
        if (expandedChangesId === radicado) {
            setExpandedChangesId(null);
            return;
        }
        setExpandedChangesId(radicado);
        if (watchNotifications[radicado]) return;
        setLoadingNotificationsFor(radicado);
        try {
            const { fetchScrapperNotifications } = await import("@/services/scraperApi");
            const rows = await fetchScrapperNotifications(radicado, 20);
            setWatchNotifications(prev => ({ ...prev, [radicado]: Array.isArray(rows) ? rows : [] }));
        } catch (err) {
            toast({ title: "Error cargando cambios", description: String(err), variant: "destructive" });
        } finally {
            setLoadingNotificationsFor(null);
        }
    };

    return (
        <div className="animate-fade-in-up">
            <PageHeader
                title={samaiMode ? "SAMAI" : "Procesos Judiciales"}
                description={samaiMode ? "Monitoreo SAMAI y vigilancia judicial (https://samai.consejodeestado.gov.co/Vistas/Casos/procesos.aspx)" : "Seguimiento de expedientes y causas"}
                icon={samaiMode ? Scale : Briefcase}
                action={samaiMode ? undefined : (
                    <div className="flex flex-wrap items-center gap-2">
                        <BulkIngestionModal onIngestionSuccess={async () => {
                            await loadScrapperProcesos(user?.id);
                            if (refetch) await refetch();
                        }} />
                        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                            <DialogTrigger asChild><Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15"><Plus className="mr-2 h-4 w-4" /> Nuevo Proceso</Button></DialogTrigger>
                            <DialogContent className="rounded-2xl">
                                <DialogHeader><DialogTitle className="text-lg font-bold">Nuevo Proceso</DialogTitle></DialogHeader>
                                <div className="grid gap-4 py-4">
                                    <div className="grid gap-2">
                                        <Label className="text-sm font-medium text-gray-600">Título del Caso *</Label>
                                        <Input className="rounded-xl" value={newProcess.title} onChange={e => setNewProcess({ ...newProcess, title: e.target.value })} />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label className="text-sm font-medium text-gray-600">Número de Expediente/Radicado *</Label>
                                        <Input className="rounded-xl" value={newProcess.case_number} onChange={e => setNewProcess({ ...newProcess, case_number: e.target.value })} />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label className="text-sm font-medium text-gray-600">Cliente</Label>
                                        <Select onValueChange={(val) => setNewProcess({ ...newProcess, client_name: val })}>
                                            <SelectTrigger className="rounded-xl"><SelectValue placeholder="Seleccionar Cliente" /></SelectTrigger>
                                            <SelectContent>
                                                {clients.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid gap-2">
                                        <Label className="text-sm font-medium text-gray-600">Descripción</Label>
                                        <Input className="rounded-xl" value={newProcess.description} onChange={e => setNewProcess({ ...newProcess, description: e.target.value })} />
                                    </div>
                                </div>
                                <DialogFooter><Button className="rounded-xl" onClick={handleCreate}>Guardar</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                )}
            />

            <Tabs value={activeProcessTab} onValueChange={setActiveProcessTab} className="w-full">
                <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-4">
                    {samaiMode ? (
                        <div className="inline-flex items-center rounded-xl bg-indigo-50 border border-indigo-100 px-3 py-2 text-sm font-semibold text-indigo-700">
                            <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse mr-2" /> Vigilancia Judicial SAMAI
                        </div>
                    ) : (
                        <TabsList className="bg-gray-100 p-1 rounded-xl h-auto flex flex-wrap gap-1 md:gap-0 justify-start w-full md:w-auto">
                            <TabsTrigger value="todos" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">Todos</TabsTrigger>
                            <TabsTrigger value="vigilancia" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
                                <span className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" /> Vigilancia Judicial
                                    <Eye className="h-5 w-5 text-gray-400" />
                                </span>
                            </TabsTrigger>
                            <TabsTrigger value="activos" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">Activos</TabsTrigger>
                            <TabsTrigger value="archivados" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">Archivados</TabsTrigger>
                        </TabsList>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Filtro por Etiquetas */}
                        <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-3 h-10 shadow-sm">
                            <Tag className="h-4 w-4 text-legal-blue shrink-0" />
                            <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">Etiqueta:</span>
                            <Select value={selectedTagFilter} onValueChange={(val: string) => setSelectedTagFilter(val)}>
                                <SelectTrigger className="h-7 border-none bg-transparent text-xs font-bold text-legal-blue focus:ring-0 focus:ring-offset-0 px-1 shadow-none">
                                    <SelectValue placeholder="Todas" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas ({processes.length})</SelectItem>
                                    {availableTags.map(t => (
                                        <SelectItem key={t} value={t}>
                                            {t} {tagCounts[t] ? `(${tagCounts[t]})` : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedTagFilter !== "all" && (
                                <button
                                    onClick={() => setSelectedTagFilter("all")}
                                    className="text-gray-400 hover:text-red-500 font-bold ml-1 text-xs px-1 rounded-full hover:bg-gray-100"
                                    title="Quitar filtro de etiqueta"
                                >
                                    &times;
                                </button>
                            )}
                        </div>

                        {activeProcessTab === "todos" && !samaiMode && (
                            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-3 h-10 shadow-sm">
                                <Layers className="h-4 w-4 text-indigo-500 shrink-0" />
                                <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">Agrupar por:</span>
                                <Select value={groupByTodos} onValueChange={(val: any) => setGroupByTodos(val)}>
                                    <SelectTrigger className="h-7 border-none bg-transparent text-xs font-bold text-indigo-700 focus:ring-0 focus:ring-offset-0 px-1 shadow-none">
                                        <SelectValue placeholder="Sin agrupar" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sin agrupar (Lista)</SelectItem>
                                        <SelectItem value="tipo">Tipo / Área (Penal, Laboral...)</SelectItem>
                                        <SelectItem value="instancia">Instancia (1ª, 2ª, Cortes...)</SelectItem>
                                        <SelectItem value="clase">Clase de Proceso</SelectItem>
                                        <SelectItem value="cliente">Cliente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <SearchBar value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar expediente..." />
                    </div>
                </div>

                {/* Tab 1: Todos (existing processes from Supabase) */}
                {!samaiMode && <TabsContent value="todos" className="space-y-4">
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <div className="w-8 h-8 border-3 border-legal-blue border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : groupByTodos !== "none" && groupedProcessesTodos ? (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between px-1">
                                <span className="text-xs font-semibold text-gray-500">
                                    Se encontraron {Object.keys(groupedProcessesTodos).length} grupo(s) para {filteredProcesses.length} proceso(s)
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-xs text-indigo-600 hover:text-indigo-800 h-7"
                                        onClick={() => {
                                            const allOpen: Record<string, boolean> = {};
                                            Object.keys(groupedProcessesTodos).forEach(k => allOpen[`todos_${k}`] = true);
                                            setExpandedGroups(prev => ({ ...prev, ...allOpen }));
                                        }}
                                    >
                                        Expandir todos
                                    </Button>
                                    <span className="text-gray-300">|</span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-xs text-gray-500 hover:text-gray-700 h-7"
                                        onClick={() => {
                                            const allClosed: Record<string, boolean> = {};
                                            Object.keys(groupedProcessesTodos).forEach(k => allClosed[`todos_${k}`] = false);
                                            setExpandedGroups(prev => ({ ...prev, ...allClosed }));
                                        }}
                                    >
                                        Colapsar todos
                                    </Button>
                                </div>
                            </div>

                            {Object.entries(groupedProcessesTodos).map(([groupTitle, groupItems]) => {
                                const isOpen = expandedGroups[`todos_${groupTitle}`] !== false;
                                return (
                                    <div key={groupTitle} className="border border-gray-200/80 rounded-2xl bg-white shadow-sm overflow-hidden transition-all">
                                        <button
                                            type="button"
                                            onClick={() => toggleGroup(`todos_${groupTitle}`)}
                                            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50/80 to-slate-50/50 hover:bg-gray-100/80 transition-colors text-left border-b border-gray-100"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600">
                                                    <FolderOpen className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                                                        {groupTitle}
                                                        <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">
                                                            {groupItems.length} {groupItems.length === 1 ? "proceso" : "procesos"}
                                                        </Badge>
                                                    </h3>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 text-gray-400">
                                                {isOpen ? <ChevronDown className="h-5 w-5 text-indigo-500" /> : <ChevronRight className="h-5 w-5" />}
                                            </div>
                                        </button>

                                        {isOpen && (
                                            <div className="p-4 space-y-3 bg-gray-50/30">
                                                {groupItems.map((process, index) => renderProcessCard(process, index))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <>
                            {paginatedProcesses.map((process, index) => renderProcessCard(process, index))}
                            {renderPaginationControls(
                                currentPageTodos,
                                totalPagesTodos,
                                filteredProcesses.length,
                                pageSizeTodos,
                                setCurrentPageTodos,
                                setPageSizeTodos
                            )}
                        </>
                    )}
                    {filteredProcesses.length === 0 && !loading && (
                        <div className="text-center py-12 text-gray-400">
                            <Briefcase className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                            <p>No hay procesos registrados.</p>
                        </div>
                    )}
                </TabsContent>}

                <TabsContent value="vigilancia" className="space-y-4">
                    {/* Tarjeta para añadir directamente a Vigilancia Judicial 
                    <Card className="border border-indigo-100 bg-gradient-to-r from-indigo-50/40 via-white to-blue-50/40 shadow-sm">
                        <CardContent className="p-3.5">
                            <div className="flex flex-col sm:flex-row items-center gap-3">
                                <div className="relative flex-1 w-full">
                                    <Input
                                        placeholder="Ingresar número de radicado (23 dígitos) para añadir a Vigilancia Judicial..."
                                        value={newRadicado}
                                        onChange={(e) => setNewRadicado(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") handleWatchRadicado();
                                        }}
                                        className="rounded-xl border-gray-200 focus:border-indigo-500 bg-white font-mono text-xs md:text-sm pl-9"
                                    />
                                    <Eye className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-indigo-400" />
                                </div>
                                <Button
                                    onClick={handleWatchRadicado}
                                    disabled={isWatching || !newRadicado || newRadicado.length < 20}
                                    className="w-full sm:w-auto rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs px-4 h-10 shrink-0 shadow-sm"
                                >
                                    {isWatching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                                    Añadir a Vigilancia
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                    */}

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <Card className="border-gray-100"><CardContent className="p-3"><p className="text-[10px] uppercase text-gray-400 font-bold">Vigilados</p><p className="text-xl font-bold text-gray-900">{vigilanciaStats.total}</p></CardContent></Card>
                        <Card className="border-emerald-100 bg-emerald-50/40"><CardContent className="p-3"><p className="text-[10px] uppercase text-emerald-500 font-bold">Actualizados</p><p className="text-xl font-bold text-emerald-700">{vigilanciaStats.updated}</p></CardContent></Card>
                        <Card className="border-indigo-100 bg-indigo-50/40"><CardContent className="p-3"><p className="text-[10px] uppercase text-indigo-500 font-bold">Con cambios</p><p className="text-xl font-bold text-indigo-700">{vigilanciaStats.withChanges}</p></CardContent></Card>
                        <Card className="border-amber-100 bg-amber-50/40"><CardContent className="p-3"><p className="text-[10px] uppercase text-amber-500 font-bold">Pausados</p><p className="text-xl font-bold text-amber-700">{vigilanciaStats.paused}</p></CardContent></Card>
                        <Card className="border-rose-100 bg-rose-50/40"><CardContent className="p-3"><p className="text-[10px] uppercase text-rose-500 font-bold">Errores</p><p className="text-xl font-bold text-rose-700">{vigilanciaStats.errors}</p></CardContent></Card>
                    </div>

                    <Card className="border border-gray-100/80">
                        <CardContent className="p-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
                            <div className="flex flex-wrap gap-2 items-center">
                                <span className="text-xs font-semibold text-gray-500">Mostrar:</span>
                                <Select value={String(pageSizeVigilancia)} onValueChange={(v: any) => { setPageSizeVigilancia(Number(v)); setCurrentPageVigilancia(1); }}>
                                    <SelectTrigger className="h-8 w-[90px] rounded-lg text-xs font-semibold">
                                        <SelectValue placeholder="10" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="10">10</SelectItem>
                                        <SelectItem value="30">30</SelectItem>
                                        <SelectItem value="50">50</SelectItem>
                                        <SelectItem value="100">100</SelectItem>
                                    </SelectContent>
                                </Select>
                                <span className="text-xs font-semibold text-gray-500 ml-1">Preset:</span>
                                <Select value={savedVigilanciaPreset} onValueChange={handleApplyPreset}>
                                    <SelectTrigger className="h-8 w-[180px] rounded-lg text-xs">
                                        <SelectValue placeholder="Preset" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="default">Vista general</SelectItem>
                                        <SelectItem value="updated_only">Solo actualizados + cambios</SelectItem>
                                        <SelectItem value="paused_only">Solo pausados</SelectItem>
                                        <SelectItem value="errors_only">Solo errores</SelectItem>
                                    </SelectContent>
                                </Select>
                                <span className="text-xs font-semibold text-gray-500">Estado:</span>
                                <Select value={watchStatusFilter} onValueChange={(v: any) => setWatchStatusFilter(v)}>
                                    <SelectTrigger className="h-8 w-[170px] rounded-lg text-xs">
                                        <SelectValue placeholder="Todos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="updated">Actualizados</SelectItem>
                                        <SelectItem value="pending">Pendientes</SelectItem>
                                        <SelectItem value="paused">Pausados</SelectItem>
                                        <SelectItem value="error">Errores</SelectItem>
                                    </SelectContent>
                                </Select>
                                <span className="text-xs font-semibold text-gray-500">Cambios:</span>
                                <Select value={changesFilter} onValueChange={(v: any) => setChangesFilter(v)}>
                                    <SelectTrigger className="h-8 w-[170px] rounded-lg text-xs">
                                        <SelectValue placeholder="Todos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="with_changes">Con cambios</SelectItem>
                                        <SelectItem value="no_changes">Sin cambios</SelectItem>
                                    </SelectContent>
                                </Select>
                                <span className="text-xs font-semibold text-gray-500 ml-1">Agrupar por:</span>
                                <Select value={groupByVigilancia} onValueChange={(v: any) => setGroupByVigilancia(v)}>
                                    <SelectTrigger className="h-8 w-[180px] rounded-lg text-xs font-bold text-indigo-700 border-indigo-200 bg-indigo-50/40">
                                        <SelectValue placeholder="Sin agrupar" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sin agrupar (Lista)</SelectItem>
                                        <SelectItem value="tipo">Tipo / Área (Penal, Laboral...)</SelectItem>
                                        <SelectItem value="instancia">Instancia (1ª, 2ª, Cortes...)</SelectItem>
                                        <SelectItem value="clase">Clase de Proceso</SelectItem>
                                        <SelectItem value="estado">Estado de Vigilancia</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs text-indigo-500 font-semibold mr-2">
                                    Auto refresh en {refreshCountdownSec}s
                                </span>
                                {isAdmin && (
                                    <div className="flex items-center gap-2 border-l border-gray-200/80 pl-2.5 ml-1">
                                        <Badge variant="outline" className="text-[10px] border-purple-200 bg-purple-50 text-purple-700 font-semibold px-2 py-0.5">
                                            Admin
                                        </Badge>
                                        <Badge className={`text-[10px] border ${cronChip.cls}`}>
                                            {cronChip.label}
                                        </Badge>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-lg h-8 text-xs bg-amber-50/60 border-amber-200 text-amber-800 hover:bg-amber-100 font-medium"
                                            onClick={handleRunVigilanciaNow}
                                            disabled={isTriggeringRunNow}
                                            title="Ejecutar la vigilancia judicial de inmediato"
                                        >
                                            {isTriggeringRunNow ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1" />}
                                            Run now (admin)
                                        </Button>
                                    </div>
                                )}
                                <span className="text-xs text-gray-500 mr-2">
                                    Último cron exitoso:{" "}
                                    <span className="font-semibold text-gray-700">
                                        {vigilanciaRunStatus?.finished_at
                                            ? new Date(vigilanciaRunStatus.finished_at).toLocaleString()
                                            : "N/D"}
                                    </span>
                                </span>
                                {!vigilanciaRunStatus?.finished_at && (
                                    <span className="text-[11px] text-gray-500 mr-2">
                                        {vigilanciaRunStatus?.message || "Inicia worker + beat o usa Run now."}
                                    </span>
                                )}
                                <div className="flex items-center gap-2 mr-2">
                                    <Checkbox
                                        checked={filteredScrapperProcesos.length > 0 && selectedWatchIds.size === filteredScrapperProcesos.length}
                                        onCheckedChange={(v: any) => handleSelectAllFiltered(Boolean(v))}
                                    />
                                    <span className="text-xs text-gray-500">Seleccionar todos</span>
                                </div>
                                {isAdmin && (
                                    <Button size="sm" variant="outline" className="rounded-lg h-8 text-xs bg-indigo-50/50 text-indigo-700 border-indigo-200 hover:bg-indigo-100" disabled={!selectedWatchIds.size || isBulkRunning} onClick={() => handleBulkAction("sync")}>
                                        <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isBulkRunning ? "animate-spin" : ""}`} /> Sync masivo (admin)
                                    </Button>
                                )}
                                <Button size="sm" variant="outline" className="rounded-lg h-8 text-xs" disabled={!selectedWatchIds.size || isBulkRunning} onClick={() => handleBulkAction("pause")}>
                                    Pausar
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-lg h-8 text-xs" disabled={!selectedWatchIds.size || isBulkRunning} onClick={() => handleBulkAction("resume")}>
                                    Reactivar
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Scrapper process list */}
                    {scrapperLoading ? (
                        <div className="flex justify-center p-8"><div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
                    ) : filteredScrapperProcesos.length > 0 ? (
                        groupByVigilancia !== "none" && groupedScrapperProcesos ? (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between px-1">
                                    <span className="text-xs font-semibold text-gray-500">
                                        Se encontraron {Object.keys(groupedScrapperProcesos).length} grupo(s) para {filteredScrapperProcesos.length} radicado(s) vigilado(s)
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-xs text-indigo-600 hover:text-indigo-800 h-7"
                                            onClick={() => {
                                                const allOpen: Record<string, boolean> = {};
                                                Object.keys(groupedScrapperProcesos).forEach(k => allOpen[`vig_${k}`] = true);
                                                setExpandedGroups(prev => ({ ...prev, ...allOpen }));
                                            }}
                                        >
                                            Expandir todos
                                        </Button>
                                        <span className="text-gray-300">|</span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-xs text-gray-500 hover:text-gray-700 h-7"
                                            onClick={() => {
                                                const allClosed: Record<string, boolean> = {};
                                                Object.keys(groupedScrapperProcesos).forEach(k => allClosed[`vig_${k}`] = false);
                                                setExpandedGroups(prev => ({ ...prev, ...allClosed }));
                                            }}
                                        >
                                            Colapsar todos
                                        </Button>
                                    </div>
                                </div>

                                {Object.entries(groupedScrapperProcesos).map(([groupTitle, groupItems]) => {
                                    const isOpen = expandedGroups[`vig_${groupTitle}`] !== false;
                                    const changesInGroup = groupItems.filter(p => (p.change_count_last_run || 0) > 0).length;

                                    return (
                                        <div key={groupTitle} className="border border-gray-200/80 rounded-2xl bg-white shadow-sm overflow-hidden transition-all">
                                            <button
                                                type="button"
                                                onClick={() => toggleGroup(`vig_${groupTitle}`)}
                                                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50/40 via-white to-slate-50/50 hover:bg-indigo-50/60 transition-colors text-left border-b border-gray-100"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 rounded-xl bg-indigo-100 text-indigo-700">
                                                        <FolderOpen className="h-5 w-5" />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                                                            {groupTitle}
                                                            <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">
                                                                {groupItems.length} {groupItems.length === 1 ? "expediente" : "expedientes"}
                                                            </Badge>
                                                            {changesInGroup > 0 && (
                                                                <Badge className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200">
                                                                    ⚡ {changesInGroup} con cambios
                                                                </Badge>
                                                            )}
                                                        </h3>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-400">
                                                    {isOpen ? <ChevronDown className="h-5 w-5 text-indigo-500" /> : <ChevronRight className="h-5 w-5" />}
                                                </div>
                                            </button>

                                            {isOpen && (
                                                <div className="p-4 space-y-3 bg-gray-50/30">
                                                    {groupItems.map((p, index) => renderScrapperProcessCard(p, index))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <>
                                {paginatedScrapperProcesos.map((p, index) => renderScrapperProcessCard(p, index))}
                                {renderPaginationControls(
                                    currentPageVigilancia,
                                    totalPagesVigilancia,
                                    filteredScrapperProcesos.length,
                                    pageSizeVigilancia,
                                    setCurrentPageVigilancia,
                                    setPageSizeVigilancia
                                )}
                            </>
                        )
                    ) : (
                        <div className="text-center py-12 text-gray-400">
                            <Scale className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                            <p className="font-medium">No hay procesos en el sistema de vigilancia.</p>
                            <p className="text-sm text-gray-300 mt-1">Agrega un radicado arriba o usa la Búsqueda Judicial para encontrar procesos.</p>
                        </div>
                    )}
                </TabsContent>

                {!samaiMode && <TabsContent value="activos">
                    <div className="space-y-4">
                        {filteredProcesses.filter(p => p.status !== "archived").map((process, index) => renderProcessCard(process, index))}
                        {filteredProcesses.filter(p => p.status !== "archived").length === 0 && (
                            <p className="text-gray-400 text-center py-8">No hay procesos activos.</p>
                        )}
                    </div>
                </TabsContent>}

                {!samaiMode && <TabsContent value="archivados">
                    <div className="space-y-4">
                        {filteredProcesses.filter(p => p.status === "archived").map((process, index) => renderProcessCard(process, index))}
                        {filteredProcesses.filter(p => p.status === "archived").length === 0 && (
                            <p className="text-gray-400 text-center py-8">No hay procesos archivados.</p>
                        )}
                    </div>
                </TabsContent>}
            </Tabs>
        </div>
    );
};

export const SamaiPage = () => <SamaiJudicialPage />;

// --- Trámites Page ---

type LegalTaskRow = {
    id: string;
    case_id: string | null;
    title: string;
    description?: string | null;
    status: string;
    due_date?: string | null;
    created_at?: string | null;
};

const isCaseTramiteRow = (desc?: string | null) => String(desc || "").includes("[TRAMITE]");
const isListTareaRow = (desc?: string | null) => {
    const d = String(desc || "");
    if (d.includes("[TRAMITE]") || d.includes("[ADELANTO]") || d.includes("[PARTE]")) return false;
    return true;
};

export const TramitesPage = () => {
    const { tramites, loading, addTramite, updateTramite, processes } = useDespachos();
    const { user } = useAuth();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newTramite, setNewTramite] = useState({
        case_id: "",
        title: "",
        entity: "",
        responsible: "",
        status: "pending" as const,
        due_date: "",
    });
    const [selectedCaseId, setSelectedCaseId] = useState<string>("");
    const [legalTasks, setLegalTasks] = useState<LegalTaskRow[]>([]);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const { toast } = useToast();

    const loadLegalTasks = async () => {
        if (!user?.id) return;
        setLoadingTasks(true);
        try {
            const { data, error } = await supabase
                .from("legal_tasks")
                .select("*")
                .eq("user_id", user.id)
                .order("created_at", { ascending: false });
            if (error) throw error;
            setLegalTasks((data || []) as LegalTaskRow[]);
        } catch (e: any) {
            toast({ title: "Error", description: e.message || "No se pudieron cargar trámites por expediente", variant: "destructive" });
        } finally {
            setLoadingTasks(false);
        }
    };

    useEffect(() => {
        loadLegalTasks();
    }, [user?.id]);

    const caseTramites = useMemo(() => {
        return legalTasks.filter((row) => {
            if (!isCaseTramiteRow(row.description)) return false;
            if (selectedCaseId && row.case_id !== selectedCaseId) return false;
            return true;
        });
    }, [legalTasks, selectedCaseId]);

    const getCaseLabel = (caseId: string | null) => {
        if (!caseId) return "—";
        const p = processes.find((x) => x.id === caseId);
        return p ? `${p.title} (${p.case_number})` : caseId.slice(0, 8) + "…";
    };

    const parseTramiteResponsible = (desc?: string | null) => {
        const m = String(desc || "").match(/responsable=([^\n]+)/);
        return m?.[1]?.trim() || "—";
    };

    const isUrgent = (dateStr?: string) => {
        if (!dateStr) return false;
        const dueDate = new Date(dateStr);
        const today = new Date();
        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 3;
    };

    const handleCreate = async () => {
        if (!newTramite.title) {
            toast({ title: "Error", description: "El título es obligatorio", variant: "destructive" });
            return;
        }
        if (!newTramite.case_id) {
            toast({ title: "Expediente requerido", description: "Seleccione el proceso al que pertenece este trámite.", variant: "destructive" });
            return;
        }
        try {
            const { error } = await supabase.from("legal_tasks").insert([
                {
                    case_id: newTramite.case_id,
                    title: newTramite.title,
                    description: `[TRAMITE] entidad=${newTramite.entity || "—"}\nresponsable=${newTramite.responsible || "Dirección"}`,
                    status: newTramite.status,
                    priority: "medium",
                    due_date: newTramite.due_date || new Date().toISOString(),
                    user_id: user?.id,
                },
            ]);
            if (error) throw error;
            await loadLegalTasks();
            toast({ title: "Trámite registrado", description: "Quedó vinculado al expediente seleccionado." });
            setIsDialogOpen(false);
            setNewTramite({ case_id: "", title: "", entity: "", responsible: "", status: "pending", due_date: "" });
        } catch (e: any) {
            toast({ title: "Error", description: e.message || "No se pudo guardar", variant: "destructive" });
        }
    };

    const handleCreateAdmin = async () => {
        if (!newTramite.title) {
            toast({ title: "Error", description: "El título es obligatorio", variant: "destructive" });
            return;
        }
        await addTramite({
            title: newTramite.title,
            entity: newTramite.entity,
            responsible: newTramite.responsible,
            status: newTramite.status,
            due_date: newTramite.due_date || undefined,
            description: "Trámite administrativo general",
        });
        setIsDialogOpen(false);
        setNewTramite({ case_id: "", title: "", entity: "", responsible: "", status: "pending", due_date: "" });
    };

    const toggleStatusLegalTask = async (id: string, currentStatus: string) => {
        const nextStatus =
            currentStatus === "pending" ? "in_progress" : currentStatus === "in_progress" ? "completed" : "pending";
        try {
            const { error } = await supabase.from("legal_tasks").update({ status: nextStatus }).eq("id", id);
            if (error) throw error;
            await loadLegalTasks();
        } catch (e: any) {
            toast({ title: "Error", description: e.message || "No se pudo actualizar", variant: "destructive" });
        }
    };

    const toggleStatus = async (id: string, currentStatus: string, source: "legal" | "admin") => {
        if (source === "legal") {
            await toggleStatusLegalTask(id, currentStatus);
            return;
        }
        const nextStatus = currentStatus === "pending" ? "in_progress" : currentStatus === "in_progress" ? "completed" : "pending";
        await updateTramite(id, { status: nextStatus });
    };

    const adminTramites = selectedCaseId ? [] : tramites;

    return (
        <div className="animate-fade-in-up space-y-4">
            <PageHeader
                title="Trámites"
                description="Por expediente (vinculados a un proceso) y generales del despacho."
                icon={Clock}
                action={
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15">
                                <Plus className="mr-2 h-4 w-4" /> Nuevo Trámite
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="text-lg font-bold">Registrar Trámite</DialogTitle>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label className="text-sm font-medium text-gray-600">Vincular a expediente *</Label>
                                    <Select value={newTramite.case_id || "none"} onValueChange={(v) => setNewTramite({ ...newTramite, case_id: v === "none" ? "" : v })}>
                                        <SelectTrigger className="rounded-xl">
                                            <SelectValue placeholder="Seleccione proceso…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Solo general (sin expediente)…</SelectItem>
                                            {processes.map((p) => (
                                                <SelectItem key={p.id} value={p.id}>
                                                    {p.case_number} — {String(p.title || "")}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-[11px] text-gray-400">Elija un proceso para trámites judiciales del expediente; use “Solo general” para trámites de despacho.</p>
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-sm font-medium text-gray-600">Título del Trámite *</Label>
                                    <Input className="rounded-xl" value={newTramite.title} onChange={(e) => setNewTramite({ ...newTramite, title: e.target.value })} />
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-sm font-medium text-gray-600">Entidad</Label>
                                    <Input className="rounded-xl" value={newTramite.entity} onChange={(e) => setNewTramite({ ...newTramite, entity: e.target.value })} />
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-sm font-medium text-gray-600">Responsable</Label>
                                    <Input className="rounded-xl" value={newTramite.responsible} onChange={(e) => setNewTramite({ ...newTramite, responsible: e.target.value })} />
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-sm font-medium text-gray-600">Fecha de Vencimiento</Label>
                                    <Input className="rounded-xl" type="date" value={newTramite.due_date} onChange={(e) => setNewTramite({ ...newTramite, due_date: e.target.value })} />
                                </div>
                            </div>
                            <DialogFooter className="gap-2 flex-col sm:flex-row">
                                <Button variant="outline" className="rounded-xl" onClick={() => setIsDialogOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button variant="secondary" className="rounded-xl" onClick={handleCreateAdmin}>
                                    Guardar como general del despacho
                                </Button>
                                <Button className="rounded-xl bg-legal-blue" onClick={handleCreate} disabled={!newTramite.case_id}>
                                    Guardar en expediente
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <Card className="border border-gray-100/80 shadow-sm p-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1.5 min-w-[220px]">
                        <Label className="text-[10px] font-bold text-gray-400 uppercase">Filtrar por proceso</Label>
                        <Select value={selectedCaseId || "all"} onValueChange={(v) => setSelectedCaseId(v === "all" ? "" : v)}>
                            <SelectTrigger className="rounded-xl w-full sm:w-[320px]">
                                <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los expedientes</SelectItem>
                                {processes.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                        {p.case_number} — {String(p.title || "")}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </Card>
            <Card className="border border-gray-100/80 shadow-sm">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">Trámites por expediente</CardTitle>
                    <CardDescription>Registrados desde el expediente o esta pantalla; no se mezclan entre procesos distintos.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    {loading || loadingTasks ? (
                        <div className="p-8 flex justify-center">
                            <div className="w-6 h-6 border-3 border-legal-blue border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <div className="w-full overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-gray-100">
                                        <TableHead className="text-gray-500 font-medium">Expediente</TableHead>
                                        <TableHead className="text-gray-500 font-medium">Trámite</TableHead>
                                        <TableHead className="text-gray-500 font-medium">Entidad</TableHead>
                                        <TableHead className="text-gray-500 font-medium">Responsable</TableHead>
                                        <TableHead className="text-gray-500 font-medium">Fecha Vencimiento</TableHead>
                                        <TableHead className="text-gray-500 font-medium">Estado</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {caseTramites.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-10 text-gray-400 text-sm">
                                                No hay trámites vinculados a expedientes{selectedCaseId ? " para este proceso" : ""}.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        caseTramites.map((t) => {
                                            const urgent = t.status !== "completed" && isUrgent(t.due_date || undefined);
                                            const entMatch = String(t.description || "").match(/entidad=([^\n]+)/);
                                            const entVal = entMatch?.[1]?.trim();
                                            const entity = (entVal && entVal !== "null" && entVal !== "undefined") ? entVal : "—";
                                            return (
                                                <TableRow key={t.id} className={cn("border-gray-100 transition-colors", urgent ? "bg-amber-50/50 hover:bg-amber-50" : "hover:bg-gray-50/50")}>
                                                    <TableCell className="text-xs text-gray-600 max-w-[200px] truncate">{getCaseLabel(t.case_id)}</TableCell>
                                                    <TableCell className="font-medium">
                                                        <div className="flex items-center gap-2">
                                                            {urgent && <AlertCircle className="h-4 w-4 text-amber-500 animate-pulse" />}
                                                            {typeof t.title === 'object' ? JSON.stringify(t.title) : String(t.title || "")}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className={cn("text-gray-500", urgent && "font-medium text-amber-900")}>{typeof entity === 'object' ? JSON.stringify(entity) : String(entity || "—")}</TableCell>
                                                    <TableCell className="text-gray-500">{parseTramiteResponsible(t.description)}</TableCell>
                                                    <TableCell className={cn("text-gray-500", urgent && "text-amber-700 font-bold")}>
                                                        {t.due_date ? new Date(t.due_date).toLocaleDateString() : "-"}
                                                        {urgent && <span className="ml-2 text-[10px] uppercase font-black">¡Próximo!</span>}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge
                                                            className={`cursor-pointer rounded-lg border transition-colors ${t.status === "pending"
                                                                ? "text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100"
                                                                : t.status === "in_progress"
                                                                    ? "text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100"
                                                                    : "text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
                                                                }`}
                                                            onClick={() => toggleStatus(t.id, t.status, "legal")}
                                                        >
                                                            {t.status === "pending" ? "Pendiente" : t.status === "in_progress" ? "En Curso" : "Completado"}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
            {!selectedCaseId && (
                <Card className="border border-gray-100/80 shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">Trámites generales del despacho</CardTitle>
                        <CardDescription>Sin expediente judicial (notarías, cámara de comercio, etc.). Visible cuando el filtro es “Todos”.</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="p-8 flex justify-center">
                                <div className="w-6 h-6 border-3 border-legal-blue border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : (
                            <div className="w-full overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-gray-100">
                                            <TableHead className="text-gray-500 font-medium">Trámite</TableHead>
                                            <TableHead className="text-gray-500 font-medium">Entidad</TableHead>
                                            <TableHead className="text-gray-500 font-medium">Responsable</TableHead>
                                            <TableHead className="text-gray-500 font-medium">Fecha Vencimiento</TableHead>
                                            <TableHead className="text-gray-500 font-medium">Estado</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {adminTramites.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center py-10 text-gray-400 text-sm">
                                                    No hay trámites generales registrados.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            adminTramites.map((t) => {
                                                const urgent = t.status !== "completed" && isUrgent(t.due_date);
                                                return (
                                                    <TableRow key={t.id} className={cn("border-gray-100 transition-colors", urgent ? "bg-amber-50/50 hover:bg-amber-50" : "hover:bg-gray-50/50")}>
                                                        <TableCell className="font-medium">
                                                            <div className="flex items-center gap-2">
                                                                {urgent && <AlertCircle className="h-4 w-4 text-amber-500 animate-pulse" />}
                                                                {typeof t.title === 'object' ? JSON.stringify(t.title) : String(t.title || "")}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className={cn("text-gray-500", urgent && "font-medium text-amber-900")}>
                                                            {t.entity && t.entity !== "null" && t.entity !== "undefined"
                                                                ? (typeof t.entity === 'object' ? JSON.stringify(t.entity) : String(t.entity))
                                                                : "—"}
                                                        </TableCell>
                                                        <TableCell className="text-gray-500">
                                                            {t.responsible && t.responsible !== "null" && t.responsible !== "undefined"
                                                                ? (typeof t.responsible === 'object' ? JSON.stringify(t.responsible) : String(t.responsible))
                                                                : "—"}
                                                        </TableCell>
                                                        <TableCell className={cn("text-gray-500", urgent && "text-amber-700 font-bold")}>
                                                            {t.due_date ? new Date(t.due_date).toLocaleDateString() : "-"}
                                                            {urgent && <span className="ml-2 text-[10px] uppercase font-black">¡Próximo!</span>}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge
                                                                className={`cursor-pointer rounded-lg border transition-colors ${t.status === "pending"
                                                                    ? "text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100"
                                                                    : t.status === "in_progress"
                                                                        ? "text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100"
                                                                        : "text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
                                                                    }`}
                                                                onClick={() => toggleStatus(t.id, t.status, "admin")}
                                                            >
                                                                {t.status === "pending" ? "Pendiente" : t.status === "in_progress" ? "En Curso" : "Completado"}
                                                            </Badge>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

// --- Calendario Page ---

export const CalendarioPage = () => {
    return (
        <div className="space-y-4 animate-fade-in-up pb-8">
            <CalendarioProcesalDespachos />
        </div>
    );
};

// --- ListaTareas Page ---

export const ListaTareasPage = () => {
    const { processes } = useDespachos();
    const { user } = useAuth();
    const { toast } = useToast();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newTask, setNewTask] = useState({ case_id: "", title: "", description: "", due_date: "" });
    const [selectedCaseId, setSelectedCaseId] = useState<string>("");
    const [legalTasks, setLegalTasks] = useState<LegalTaskRow[]>([]);
    const [loadingTasks, setLoadingTasks] = useState(false);

    const loadLegalTasks = async () => {
        if (!user?.id) return;
        setLoadingTasks(true);
        try {
            const { data, error } = await supabase
                .from("legal_tasks")
                .select("*")
                .eq("user_id", user.id)
                .order("created_at", { ascending: false });
            if (error) throw error;
            setLegalTasks((data || []) as LegalTaskRow[]);
        } catch (e: any) {
            toast({ title: "Error", description: e.message || "No se pudieron cargar las tareas", variant: "destructive" });
        } finally {
            setLoadingTasks(false);
        }
    };

    useEffect(() => {
        loadLegalTasks();
    }, [user?.id]);

    const caseTareas = useMemo(() => {
        return legalTasks.filter((row) => {
            if (!isListTareaRow(row.description)) return false;
            if (selectedCaseId && row.case_id !== selectedCaseId) return false;
            return true;
        });
    }, [legalTasks, selectedCaseId]);

    const getCaseLabel = (caseId: string | null) => {
        if (!caseId) return "Sin expediente";
        const p = processes.find((x) => x.id === caseId);
        return p ? `${p.title} (${p.case_number})` : caseId.slice(0, 8) + "…";
    };

    const taskSubtitle = (row: LegalTaskRow) => {
        const d = String(row.description || "").replace(/^\[TAREA\][^\n]*\n?/, "").trim();
        if (!d) return getCaseLabel(row.case_id);
        const one = d.split("\n")[0];
        return `${getCaseLabel(row.case_id)} • ${one.length > 120 ? one.slice(0, 117) + "…" : one}`;
    };

    const handleCreate = async () => {
        if (!newTask.title) {
            toast({ title: "Título requerido", variant: "destructive" });
            return;
        }
        if (!newTask.case_id) {
            toast({ title: "Expediente requerido", description: "Seleccione el proceso al que pertenece la tarea.", variant: "destructive" });
            return;
        }
        try {
            const descLines = [`[TAREA] responsable=Dirección`];
            if (newTask.description.trim()) descLines.push(newTask.description.trim());
            const { error } = await supabase.from("legal_tasks").insert([
                {
                    case_id: newTask.case_id,
                    title: newTask.title,
                    description: descLines.join("\n"),
                    status: "pending",
                    priority: "medium",
                    due_date: newTask.due_date || new Date().toISOString(),
                    user_id: user?.id,
                },
            ]);
            if (error) throw error;
            await loadLegalTasks();
            toast({ title: "Tarea creada" });
            setIsDialogOpen(false);
            setNewTask({ case_id: "", title: "", description: "", due_date: "" });
        } catch (e: any) {
            toast({ title: "Error", description: e.message || "No se pudo guardar", variant: "destructive" });
        }
    };

    const handleToggleStatus = async (id: string, currentStatus: string) => {
        const newStatus = currentStatus === "completed" ? "pending" : "completed";
        try {
            const { error } = await supabase.from("legal_tasks").update({ status: newStatus }).eq("id", id);
            if (error) throw error;
            await loadLegalTasks();
        } catch (e: any) {
            toast({ title: "Error", description: e.message || "No se pudo actualizar", variant: "destructive" });
        }
    };

    return (
        <div className="max-w-4xl mx-auto animate-fade-in-up space-y-4">
            <PageHeader
                title="Lista de Tareas"
                description="Tareas vinculadas a expedientes (no se mezclan entre procesos)."
                icon={CheckCircle2}
                action={
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15">
                                <Plus className="mr-2 h-4 w-4" /> Nueva Tarea
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-2xl">
                            <DialogHeader>
                                <DialogTitle className="text-lg font-bold">Nueva Tarea</DialogTitle>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label className="text-sm font-medium text-gray-600">Expediente *</Label>
                                    <Select value={newTask.case_id || "none"} onValueChange={(v) => setNewTask({ ...newTask, case_id: v === "none" ? "" : v })}>
                                        <SelectTrigger className="rounded-xl">
                                            <SelectValue placeholder="Seleccione proceso…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Seleccione proceso…</SelectItem>
                                            {processes.map((p) => (
                                                <SelectItem key={p.id} value={p.id}>
                                                    {p.case_number} — {String(p.title || "")}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Input className="rounded-xl" placeholder="Título de la tarea" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} />
                                <Textarea className="rounded-xl min-h-[88px]" placeholder="Descripción (opcional)" value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} />
                                <div className="grid gap-2">
                                    <Label className="text-sm font-medium text-gray-600">Vencimiento (opcional)</Label>
                                    <Input className="rounded-xl" type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button className="rounded-xl" onClick={handleCreate}>
                                    Guardar
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <Card className="border border-gray-100/80 shadow-sm p-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1.5 min-w-[220px]">
                        <Label className="text-[10px] font-bold text-gray-400 uppercase">Filtrar por proceso</Label>
                        <Select value={selectedCaseId || "all"} onValueChange={(v) => setSelectedCaseId(v === "all" ? "" : v)}>
                            <SelectTrigger className="rounded-xl w-full sm:w-[320px]">
                                <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los expedientes</SelectItem>
                                {processes.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                        {p.case_number} — {p.title}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </Card>
            <Card className="border border-gray-100/80 shadow-sm">
                <CardContent className="p-0 divide-y divide-gray-100">
                    {loadingTasks ? (
                        <div className="p-12 flex justify-center">
                            <div className="w-6 h-6 border-3 border-legal-blue border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : caseTareas.length === 0 ? (
                        <div className="p-12 text-center">
                            <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                                <CheckCircle2 className="h-7 w-7 text-gray-300" />
                            </div>
                            <p className="text-gray-400 text-sm">No hay tareas para este filtro.</p>
                        </div>
                    ) : (
                        caseTareas.map((t) => {
                            const isCompleted = t.status === "completed";
                            const urgent =
                                !isCompleted &&
                                t.due_date &&
                                new Date(t.due_date).getTime() - new Date().getTime() < 3 * 24 * 60 * 60 * 1000;
                            return (
                                <div
                                    key={t.id}
                                    className={cn(
                                        "flex items-center gap-4 p-4 transition-colors",
                                        urgent ? "bg-amber-50/40 hover:bg-amber-50" : isCompleted ? "bg-gray-50/50" : "hover:bg-blue-50/30"
                                    )}
                                >
                                    <Checkbox
                                        checked={isCompleted}
                                        onCheckedChange={() => handleToggleStatus(t.id, t.status)}
                                        className="rounded-md h-5 w-5 border-gray-300 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p
                                                className={cn(
                                                    "font-medium transition-all duration-300",
                                                    isCompleted ? "text-gray-400 line-through" : urgent ? "text-amber-900" : "text-gray-900"
                                                )}
                                            >
                                                {t.title}
                                            </p>
                                            {urgent && <Badge className="bg-amber-100 text-amber-700 text-[10px] h-4 px-1 animate-pulse">URGENTE</Badge>}
                                        </div>
                                        <p className={cn("text-xs transition-colors truncate", isCompleted ? "text-gray-300" : "text-gray-400")}>{taskSubtitle(t)}</p>
                                    </div>
                                    <Badge
                                        className={cn(
                                            "px-2 py-0.5 rounded-lg border text-[10px] font-bold transition-all duration-300 shrink-0",
                                            isCompleted
                                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                : t.status === "in_progress"
                                                    ? "bg-blue-50 text-blue-700 border-blue-200"
                                                    : "bg-amber-50 text-amber-700 border-amber-200"
                                        )}
                                    >
                                        {isCompleted ? "Completado" : t.status === "in_progress" ? "En Curso" : "Pendiente"}
                                    </Badge>
                                </div>
                            );
                        })
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

// --- Contactos Page ---

// --- Contactos Page ---

export const ContactosPage = () => {
    const { contacts, addContact, deleteContact, loading } = useDespachos();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formData, setFormData] = useState({ name: "", role: "Abogado", email: "", phone: "", organization: "" });
    const [searchTerm, setSearchTerm] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const { toast } = useToast();

    const handleCreate = async () => {
        if (!formData.name) return;
        await addContact(formData);
        setIsDialogOpen(false);
        setFormData({ name: "", role: "Abogado", email: "", phone: "", organization: "" });
    };

    const handleDelete = async (id: string) => {
        if (confirm("¿Estás seguro de eliminar este contacto?")) {
            await deleteContact(id);
        }
    };

    const filteredContacts = contacts.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.role && c.role.toLowerCase().includes(searchTerm.toLowerCase())) ||
        ((c as any).organization && (c as any).organization.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleImport = () => {
        if (file) {
            toast({ title: "Importando", description: "Procesando archivo..." });
            setFile(null);
        }
    };

    return (
        <div className="animate-fade-in-up">
            <PageHeader title="Directorio de Contactos" icon={BookUser} action={
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild><Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15"><Plus className="mr-2 h-4 w-4" /> Nuevo Contacto</Button></DialogTrigger>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader><DialogTitle className="text-lg font-bold">Nuevo Contacto</DialogTitle></DialogHeader>
                        <div className="grid gap-4 py-4">
                            <Input className="rounded-xl" placeholder="Nombre Completo" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                            <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Rol" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Juez">Juez</SelectItem>
                                    <SelectItem value="Abogado">Abogado</SelectItem>
                                    <SelectItem value="Fiscal">Fiscal</SelectItem>
                                    <SelectItem value="Secretario">Secretario</SelectItem>
                                    <SelectItem value="Perito">Perito</SelectItem>
                                    <SelectItem value="Cliente">Cliente (Externo)</SelectItem>
                                    <SelectItem value="Otro">Otro</SelectItem>
                                </SelectContent>
                            </Select>
                            <Input className="rounded-xl" placeholder="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                            <Input className="rounded-xl" placeholder="Teléfono" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                            <Input className="rounded-xl" placeholder="Organización / Juzgado" value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} />
                        </div>
                        <Button className="w-full rounded-xl" onClick={handleCreate}>Guardar Contacto</Button>
                    </DialogContent>
                </Dialog>
            } />

            <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="relative flex-1 w-full md:w-auto">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        placeholder="Buscar por nombre, rol o entidad..."
                        className="pl-9 w-full md:w-80 rounded-xl border-gray-200"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex gap-2 items-center">
                    <Input type="file" className="w-[200px] rounded-xl" onChange={handleFileChange} accept=".csv" />
                    <Button variant="outline" size="sm" className="rounded-xl" onClick={handleImport} disabled={!file}>Importar</Button>
                </div>
            </div>

            <Card className="border border-gray-100/80 shadow-sm">
                <div className="w-full overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100">
                                <TableHead className="text-gray-500 font-medium">Nombre</TableHead>
                                <TableHead className="text-gray-500 font-medium">Rol</TableHead>
                                <TableHead className="text-gray-500 font-medium">Entidad</TableHead>
                                <TableHead className="text-gray-500 font-medium">Contacto</TableHead>
                                <TableHead className="text-right text-gray-500 font-medium">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ?
                                <TableRow><TableCell colSpan={5} className="text-center py-6"><div className="w-6 h-6 border-3 border-legal-blue border-t-transparent rounded-full animate-spin mx-auto" /></TableCell></TableRow>
                                : filteredContacts.length === 0 ?
                                    <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-12">No se encontraron contactos.</TableCell></TableRow>
                                    : filteredContacts.map((contact) => (
                                        <TableRow key={contact.id} className="border-gray-100 hover:bg-blue-50/30 transition-colors">
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white flex items-center justify-center font-bold text-xs">
                                                        {contact.name ? contact.name.substring(0, 2).toUpperCase() : "??"}
                                                    </div>
                                                    {contact.name}
                                                </div>
                                            </TableCell>
                                            <TableCell><Badge className="bg-blue-50 text-blue-700 border border-blue-200">{contact.role}</Badge></TableCell>
                                            <TableCell className="text-gray-500">{(contact as any).organization || "-"}</TableCell>
                                            <TableCell>
                                                <div className="flex flex-col text-xs text-gray-500">
                                                    <span>{contact.email}</span>
                                                    <span>{contact.phone}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="icon" className="rounded-xl hover:bg-red-50" onClick={() => handleDelete(contact.id)}>
                                                    <Trash2 className="h-4 w-4 text-red-400" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    )
};

// --- EnviarEmail Page ---

export const EnviarEmailPage = () => (
    <div className="max-w-2xl mx-auto animate-fade-in-up">
        <PageHeader title="Redactar Correo" icon={Mail} />
        <Card className="border border-gray-100/80 shadow-sm">
            <CardContent className="p-6 space-y-4">
                <div className="grid gap-2">
                    <span className="text-sm font-medium text-gray-600">Para:</span>
                    <Input className="rounded-xl" placeholder="destinatario@ejemplo.com" />
                </div>
                <div className="grid gap-2">
                    <span className="text-sm font-medium text-gray-600">Asunto:</span>
                    <Input className="rounded-xl" placeholder="Asunto del correo" />
                </div>
                <div className="grid gap-2">
                    <span className="text-sm font-medium text-gray-600">Mensaje:</span>
                    <textarea className="min-h-[200px] w-full rounded-xl border border-gray-200 bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-legal-blue" placeholder="Escribe tu mensaje aquí..." />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" className="rounded-xl">Descartar</Button>
                    <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy"><Mail className="mr-2 h-4 w-4" /> Enviar</Button>
                </div>
            </CardContent>
        </Card>
    </div>
);

// --- Archivos Pages ---

export const ArchivosDespachoPage = () => {
    const { user } = useAuth();
    const { toast } = useToast();
    const { uploadFile, deleteFile, files: genericFiles, loading: genericLoading, processes } = useDespachos();
    const [procesoFiles, setProcesoFiles] = useState<scraperApi.ProcesoArchivo[]>([]);
    const [isLoadingProcesos, setIsLoadingProcesos] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedFolder, setSelectedFolder] = useState<string | null>(null);

    // Viewing State
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [viewMode, setViewMode] = useState<"editor" | "viewer" | "image">("editor");
    const [selectedViewFile, setSelectedViewFile] = useState<any>(null);
    const [fileUrl, setFileUrl] = useState<string>("");

    // Linking State
    const [isLinking, setIsLinking] = useState(false);
    const [selectedFile, setSelectedFile] = useState<any>(null);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkSearch, setLinkSearch] = useState("");
    const [vectorizeOnLink, setVectorizeOnLink] = useState(false);

    const openViewer = async (file: any) => {
        setSelectedViewFile(file);
        const nameLower = file.name?.toLowerCase() || "";
        const isPdf = nameLower.endsWith('.pdf');
        const isImg = nameLower.endsWith('.png') || nameLower.endsWith('.jpg') || nameLower.endsWith('.jpeg') || nameLower.endsWith('.webp') || nameLower.endsWith('.gif') || nameLower.endsWith('.svg');

        if (user) {
            const path = user.id + "/" + file.name;
            const { data, error } = await supabase.storage.from('gestion_misarchivos').createSignedUrl(path, 3600);
            if (data) {
                setFileUrl(data.signedUrl);
                if (isPdf) {
                    setViewMode("viewer");
                } else if (isImg) {
                    setViewMode("image");
                } else {
                    setViewMode("editor");
                }
                setIsViewerOpen(true);
            } else {
                console.error("Error creating signed url", error);
            }
        }
    };

    const handleSave = async (name: string, content: string) => {
        const blob = new Blob([content], { type: "text/plain" });
        const file = new window.File([blob], name, { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
        await uploadFile(file);
    };

    const loadFiles = async () => {
        setIsLoadingProcesos(true);
        try {
            const files = await scraperApi.getAllProcesoFiles();
            setProcesoFiles(files);
        } catch (error) {
            console.error("Error loading all process files:", error);
        } finally {
            setIsLoadingProcesos(false);
        }
    };

    useEffect(() => {
        loadFiles();
    }, []);

    const getFolderTerm = (folderName: string) => {
        if (folderName === "Facturación") return "factura";
        if (folderName === "Administrativo") return "admin";
        return folderName.toLowerCase().endsWith("s") ? folderName.toLowerCase().slice(0, -1) : folderName.toLowerCase();
    };

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (selectedFolder) {
                const term = getFolderTerm(selectedFolder);
                const prefix = `${term}_`;
                if (!file.name.toLowerCase().startsWith(prefix)) {
                    uploadFile(file, `${prefix}${file.name}`);
                    return;
                }
            }
            uploadFile(file);
        }
    };

    const handleLinkFile = async (numeroProceso: string) => {
        if (!user) {
            alert("Error: Usuario no detectado");
            return;
        }
        if (!selectedFile) {
            alert("Error: Archivo no seleccionado");
            return;
        }
        alert(`Iniciando vinculación de ${selectedFile.name} a radicado ${numeroProceso}`);
        setIsLinking(true);
        try {
            await scraperApi.linkFileToProceso(user.id, selectedFile.name, numeroProceso, vectorizeOnLink);
            toast({ title: "Archivo vinculado", description: `${selectedFile.name} ahora está en el expediente ${numeroProceso}.` });
            setLinkDialogOpen(false);
            setVectorizeOnLink(false); // Reset
            loadFiles(); // Refresh process files
        } catch (error: any) {
            toast({ variant: "destructive", title: "Error al vincular", description: error.message });
        } finally {
            setIsLinking(false);
        }
    };

    const folders = ["Contratos", "Demandas", "Tutelas", "Administrativo", "Personal", "Facturación"];

    const getFolderCount = (folderName: string) => {
        const term = getFolderTerm(folderName);
        const genCount = genericFiles.filter(f => f.name.toLowerCase().includes(term)).length;
        const procCount = procesoFiles.filter(f => f.file_name.toLowerCase().includes(term)).length;
        return genCount + procCount;
    };

    // Filter logic
    const filteredGeneric = useMemo(() => {
        let list = genericFiles;
        if (selectedFolder) {
            const term = getFolderTerm(selectedFolder);
            list = list.filter(f => f.name.toLowerCase().includes(term));
        }
        if (searchTerm) {
            list = list.filter(f => f.name.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        return list;
    }, [genericFiles, selectedFolder, searchTerm]);

    const filteredProceso = useMemo(() => {
        let list = procesoFiles;
        if (selectedFolder) {
            const term = getFolderTerm(selectedFolder);
            list = list.filter(f => f.file_name.toLowerCase().includes(term));
        }
        if (searchTerm) {
            list = list.filter(f =>
                f.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                f.procesos?.numero_proceso.includes(searchTerm) ||
                f.procesos?.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return list;
    }, [procesoFiles, selectedFolder, searchTerm]);

    if (isViewerOpen && selectedViewFile) {
        return (
            <div className="h-full flex flex-col animate-fade-in">
                {viewMode === "viewer" ? (
                    <PdfViewer
                        fileName={selectedViewFile.name}
                        fileUrl={fileUrl}
                        onClose={() => setIsViewerOpen(false)}
                    />
                ) : viewMode === "image" ? (
                    <ImageViewer
                        fileName={selectedViewFile.name}
                        fileUrl={fileUrl}
                        onClose={() => setIsViewerOpen(false)}
                    />
                ) : (
                    <DocumentEditor
                        fileName={selectedViewFile.name}
                        fileUrl={fileUrl}
                        onClose={() => setIsViewerOpen(false)}
                        onSave={handleSave}
                        onCopy={handleSave}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="animate-fade-in-up space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div className="flex-1 max-w-xl">
                    <PageHeader title="Archivos del Despacho" icon={FolderIcon} />
                    {selectedFolder && (
                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedFolder(null)}
                                className="h-7 px-2 hover:bg-gray-150 text-xs font-semibold rounded-lg flex items-center gap-1.5"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" /> Carpetas
                            </Button>
                            <span className="text-gray-300">/</span>
                            <span className="font-bold text-gray-855">{selectedFolder}</span>
                        </div>
                    )}
                    <div className="relative mt-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            className="pl-10 rounded-xl bg-white border-gray-100 shadow-sm focus:ring-legal-blue"
                            placeholder="Buscar en todos los archivos, procesos o radicados..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="relative">
                    <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15 w-full md:w-auto">
                        <Upload className="mr-2 h-4 w-4" />
                        {selectedFolder ? `Subir a ${selectedFolder}` : "Subir Archivo"}
                    </Button>
                    <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleUpload} />
                </div>
            </div>

            <div>
                {!selectedFolder ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {folders.map((folderName, index) => (
                            <div
                                key={folderName}
                                onClick={() => setSelectedFolder(folderName)}
                                className={`flex flex-col items-center p-5 border border-gray-100 rounded-2xl hover:bg-blue-50/30 hover:border-blue-200 hover:shadow-md hover:-translate-y-0.5 cursor-pointer transition-all duration-300 group animate-fade-in-up stagger-${index + 1}`}
                            >
                                <FolderIcon className="h-10 w-10 text-blue-200 group-hover:text-blue-400 mb-2 fill-current transition-colors" />
                                <span className="text-sm font-medium text-gray-900 text-center">{folderName}</span>
                                <span className="text-[10px] text-gray-400 uppercase tracking-tighter">{getFolderCount(folderName)} archivos</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Folder details banner */}
                        <div className="md:col-span-2 flex items-center gap-4 p-6 bg-gradient-to-r from-blue-50/40 to-indigo-50/20 border border-blue-100/50 rounded-2xl animate-fade-in">
                            <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center shadow-inner">
                                <FolderIcon className="h-7 w-7 fill-current" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">{selectedFolder}</h3>
                                <p className="text-xs text-gray-400">
                                    Esta carpeta contiene {getFolderCount(selectedFolder)} archivos en total. Los archivos subidos aquí se prefijan con <code className="bg-blue-50 border border-blue-100 text-[10px] text-blue-600 px-1 py-0.5 rounded font-mono font-bold">{getFolderTerm(selectedFolder)}_</code>.
                                </p>
                            </div>
                        </div>

                        {/* Interactive Drag & Drop Area */}
                        <Card className="border border-dashed border-indigo-200/80 bg-indigo-50/10 p-5 text-center rounded-2xl flex flex-col items-center justify-center hover:bg-indigo-50/20 transition-all duration-300 relative overflow-hidden group min-h-[110px]">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                            <Upload className="h-7 w-7 text-indigo-400 group-hover:text-indigo-600 mb-2 transition-colors animate-pulse" />
                            <h4 className="font-bold text-xs text-indigo-950 mb-0.5">Subir a {selectedFolder}</h4>
                            <p className="text-[10px] text-indigo-500/70 max-w-xs mb-2">Clasificación automática habilitada</p>
                            <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleUpload} />
                        </Card>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Generic Desk Files */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-md font-bold text-gray-900">Archivos de Gestión</h3>
                            <p className="text-xs text-gray-400 font-medium">{searchTerm ? `Resultados para "${searchTerm}"` : (selectedFolder ? `Documentos en carpeta ${selectedFolder}` : 'Documentos generales del despacho')}</p>
                        </div>
                    </div>
                    <Card className="border border-gray-100/80 shadow-sm overflow-hidden">
                        {genericLoading ? (
                            <div className="p-8 text-center"><Loader2 className="h-6 w-6 text-legal-blue animate-spin mx-auto" /></div>
                        ) : filteredGeneric.length > 0 ? (
                            <div className="w-full overflow-x-auto">
                                <Table>
                                    <TableBody>
                                        {filteredGeneric.slice(0, 10).map((file) => (
                                            <TableRow
                                                key={file.id}
                                                className="hover:bg-gray-50/50 cursor-pointer transition-colors group"
                                                onClick={() => openViewer(file)}
                                            >
                                                <TableCell className="py-3">
                                                    <div className="flex items-center gap-3">
                                                        <FileText className="h-4 w-4 text-blue-400" />
                                                        <span className="text-sm font-medium text-gray-700 truncate max-w-[150px]">{file.name}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-[10px] rounded-lg text-indigo-650 font-bold"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openViewer(file);
                                                            }}
                                                        >
                                                            {/\.(pdf|png|jpe?g|webp|gif|svg)$/i.test(file.name || "") ? 'Ver' : 'Editar'}
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-[10px] rounded-lg"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedFile(file);
                                                                setLinkDialogOpen(true);
                                                            }}
                                                        >
                                                            Vincular
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (window.confirm(`¿Estás seguro de que deseas eliminar el archivo "${file.name}"?`)) {
                                                                    deleteFile(file.name);
                                                                }
                                                            }}
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right text-[10px] text-gray-400">{new Date(file.created_at).toLocaleDateString()}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="p-8 text-center text-xs text-gray-400">No se encontraron archivos en esta sección.</div>
                        )}
                    </Card>
                </div>

                {/* Process Linked Files */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-md font-bold text-gray-900">Archivos por Expediente</h3>
                            <p className="text-xs text-gray-400 font-medium">{searchTerm ? `Encontrados en radicados` : (selectedFolder ? `Archivos de expedientes en ${selectedFolder}` : 'Vinculados a radicados judiciales')}</p>
                        </div>
                    </div>
                    <Card className="border border-gray-100/80 shadow-sm overflow-hidden">
                        {isLoadingProcesos ? (
                            <div className="p-8 text-center"><Loader2 className="h-6 w-6 text-legal-blue animate-spin mx-auto" /></div>
                        ) : filteredProceso.length > 0 ? (
                            <div className="w-full overflow-x-auto">
                                <Table>
                                    <TableBody>
                                        {filteredProceso.slice(0, 10).map((file) => (
                                            <TableRow key={file.id} className="hover:bg-gray-50/50 transition-colors">
                                                <TableCell className="py-3">
                                                    <div className="flex items-center gap-3">
                                                        <FileText className="h-4 w-4 text-legal-blue" />
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-medium text-gray-700 truncate max-w-[150px]">{file.file_name}</span>
                                                            <span className="text-[9px] text-legal-blue font-bold opacity-70">{file.procesos?.numero_proceso}</span>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right text-[10px] text-gray-400">{new Date(file.created_at).toLocaleDateString()}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="p-8 text-center text-xs text-gray-400">No hay archivos vinculados en esta sección.</div>
                        )}
                    </Card>
                </div>
            </div>

            {/* Link to Process Dialog */}
            <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
                <DialogContent className="sm:max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-bold">Vincular a Expediente</DialogTitle>
                        <p className="text-xs text-gray-500 italic truncate">{selectedFile?.name}</p>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <Input
                                placeholder="Buscar proceso o radicado..."
                                className="pl-9 h-9 text-sm rounded-xl border-gray-100"
                                value={linkSearch}
                                onChange={(e) => setLinkSearch(e.target.value)}
                            />
                        </div>

                        <div className="flex items-center space-x-2 bg-blue-50/50 p-3 rounded-xl border border-blue-100">
                            <Checkbox
                                id="vectorize"
                                checked={vectorizeOnLink}
                                onCheckedChange={(checked) => setVectorizeOnLink(!!checked)}
                            />
                            <div className="grid gap-1.5 leading-none">
                                <label
                                    htmlFor="vectorize"
                                    className="text-xs font-bold text-legal-blue leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                    Vectorizar para este proceso
                                </label>
                                <p className="text-[10px] text-gray-400">
                                    Permite que la IA analice y responda preguntas sobre este documento.
                                </p>
                            </div>
                        </div>

                        <ScrollArea className="h-60 rounded-xl border border-gray-50 p-2">
                            <div className="space-y-1">
                                {processes.filter(p =>
                                    p.case_number.includes(linkSearch) ||
                                    p.title.toLowerCase().includes(linkSearch.toLowerCase())
                                ).map((p) => (
                                    <button
                                        key={p.id}
                                        onClick={() => {
                                            if (p.case_number) handleLinkFile(p.case_number);
                                        }}
                                        disabled={isLinking || !p.case_number}
                                        className="w-full text-left p-3 rounded-xl hover:bg-blue-50/50 transition-colors group flex items-center justify-between border border-transparent hover:border-blue-100"
                                    >
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-sm font-bold text-gray-900 truncate">{p.title}</span>
                                            <span className="text-[10px] text-gray-400 font-mono">{p.case_number}</span>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-legal-blue" />
                                    </button>
                                ))}
                            </div>
                        </ScrollArea>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export const MisArchivosPage = () => {
    const { uploadFile, files, loading } = useDespachos();
    const { user } = useAuth();
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [viewMode, setViewMode] = useState<"editor" | "viewer" | "image">("editor");
    const [selectedFile, setSelectedFile] = useState<any>(null);
    const [fileUrl, setFileUrl] = useState<string>("");

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) uploadFile(file);
    };

    const openEditor = async (file: any) => {
        setSelectedFile(file);
        const nameLower = file.name?.toLowerCase() || "";
        const isPdf = nameLower.endsWith('.pdf');
        const isImg = nameLower.endsWith('.png') || nameLower.endsWith('.jpg') || nameLower.endsWith('.jpeg') || nameLower.endsWith('.webp') || nameLower.endsWith('.gif') || nameLower.endsWith('.svg');

        if (user) {
            const path = user.id + "/" + file.name;
            const { data, error } = await supabase.storage.from('gestion_misarchivos').createSignedUrl(path, 3600);
            if (data) {
                setFileUrl(data.signedUrl);
                if (isPdf) {
                    setViewMode("viewer");
                } else if (isImg) {
                    setViewMode("image");
                } else {
                    setViewMode("editor");
                }
                setIsEditorOpen(true);
            } else {
                console.error("Error creating signed url", error);
            }
        }
    };

    const handleSave = async (name: string, content: string) => {
        const blob = new Blob([content], { type: "text/plain" });
        const file = new window.File([blob], name, { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
        await uploadFile(file);
    };

    if (isEditorOpen && selectedFile) {
        return (
            <div className="h-full flex flex-col animate-fade-in">
                {viewMode === "viewer" ? (
                    <PdfViewer
                        fileName={selectedFile.name}
                        fileUrl={fileUrl}
                        onClose={() => setIsEditorOpen(false)}
                    />
                ) : viewMode === "image" ? (
                    <ImageViewer
                        fileName={selectedFile.name}
                        fileUrl={fileUrl}
                        onClose={() => setIsEditorOpen(false)}
                    />
                ) : (
                    <DocumentEditor
                        fileName={selectedFile.name}
                        fileUrl={fileUrl}
                        onClose={() => setIsEditorOpen(false)}
                        onSave={handleSave}
                        onCopy={handleSave}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="animate-fade-in-up">
            <PageHeader title="Mis Archivos Personales" icon={FileText} action={
                <div className="relative flex gap-2">
                    <div className="relative">
                        <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md"><Upload className="mr-2 h-4 w-4" /> Subir PDF / DOCX</Button>
                        <input type="file" accept=".docx,.pdf,.txt" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleUpload} />
                    </div>
                </div>
            } />
            <Card className="border border-gray-100/80 shadow-sm">
                <div className="w-full overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100">
                                <TableHead className="w-[50%] text-gray-500 font-medium">Nombre</TableHead>
                                <TableHead className="text-gray-500 font-medium">Fecha</TableHead>
                                <TableHead className="text-gray-500 font-medium">Tamaño</TableHead>
                                <TableHead className="text-right text-gray-500 font-medium">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ?
                                <TableRow><TableCell colSpan={4} className="text-center py-6"><div className="w-6 h-6 border-3 border-legal-blue border-t-transparent rounded-full animate-spin mx-auto" /></TableCell></TableRow>
                                : files.length === 0 ?
                                    <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-12">No hay archivos cargados. Sube uno nuevo.</TableCell></TableRow>
                                    : files.map((file) => (
                                        <TableRow key={file.id} className="hover:bg-blue-50/30 cursor-pointer border-gray-100 transition-colors" onClick={() => openEditor(file)}>
                                            <TableCell className="font-medium flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                                                    {file.name?.toLowerCase().endsWith('.pdf') ? (
                                                        <File className="h-4 w-4 text-white" />
                                                    ) : (
                                                        <FileText className="h-4 w-4 text-white" />
                                                    )}
                                                </div>
                                                {file.name}
                                            </TableCell>
                                            <TableCell className="text-gray-500">{new Date(file.created_at).toLocaleDateString()}</TableCell>
                                            <TableCell className="text-gray-500">{(file.metadata?.size / 1024).toFixed(1)} KB</TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="sm" className="rounded-xl hover:bg-blue-50 text-gray-600" onClick={(e) => { e.stopPropagation(); openEditor(file); }}>
                                                    {/\.(pdf|png|jpe?g|webp|gif|svg)$/i.test(file.name || "") ? 'Ver' : 'Editar'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    )
};

export const DescargarTirantBoxPage = () => (
    <div className="flex flex-col items-center justify-center p-16 text-center animate-fade-in-up">
        <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-6 shadow-lg shadow-blue-500/20">
            <Download className="h-10 w-10 text-white" />
        </div>
        {/* <h1 className="text-2xl font-bold text-gray-900 mb-2">Descargar TirantBox</h1> */}
        <p className="text-gray-400 max-w-md mb-8 text-sm">Accede a tu repositorio documental seguro desde el escritorio con la aplicación de TirantBox.</p>
        <Button size="lg" className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md">Descargar para Windows</Button>
        <Button variant="link" className="mt-2 text-gray-400">Otras versiones (Mac, Linux)</Button>
    </div>
);

// --- Recibos Pages ---

export const ListadoRecibosPage = () => {
    const { receipts, loading } = useDespachos();
    const { toast } = useToast();

    const handleDownloadReceipt = (receipt: Receipt) => {
        try {
            const doc = new jsPDF();
            const margin = 20;
            const pageWidth = doc.internal.pageSize.getWidth();
            const textWidth = pageWidth - 2 * margin;
            let cursorY = 25;

            // Header Background
            doc.setFillColor(248, 250, 252); // slate-50
            doc.rect(0, 0, pageWidth, 50, 'F');
            doc.setFillColor(37, 99, 235); // blue-600
            doc.rect(0, 0, pageWidth, 4, 'F');

            // Brand
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.setTextColor(30, 41, 59); // slate-800
            doc.text("SarAI — INTELIGENCIA JURÍDICA", margin, cursorY);

            // Receipt Tag
            doc.setFontSize(10);
            doc.setTextColor(37, 99, 235);
            doc.text(`RECIBO DE CAJA PRE-FECHADO`, pageWidth - margin - 60, cursorY);

            cursorY += 10;
            doc.setTextColor(100, 116, 139); // slate-500
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text(`${new Date().toLocaleDateString('es-CO').toUpperCase()}`, pageWidth - margin - 60, cursorY);

            cursorY = 65;

            // Details section
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor(30, 41, 59);
            doc.text("DETALLES DEL RECIBO", margin, cursorY);
            cursorY += 8;
            doc.setDrawColor(226, 232, 240); // slate-200
            doc.line(margin, cursorY, pageWidth - margin, cursorY);
            cursorY += 12;

            // Content Fields
            const fields = [
                { label: "Número de Recibo:", value: receipt.receipt_number || "---" },
                { label: "Fecha:", value: new Date(receipt.date).toLocaleDateString('es-CO') },
                { label: "Cliente:", value: receipt.client_name || "Cliente General" },
                { label: "Concepto:", value: receipt.concept || "Servicios Legales" },
                { label: "Monto:", value: `$${receipt.amount.toLocaleString('es-CO')}`, isBold: true },
                { label: "Estado:", value: receipt.status === 'paid' ? 'PAGADO' : 'PENDIENTE', isStatus: true }
            ];

            fields.forEach((field) => {
                doc.setFont("helvetica", "bold");
                doc.setTextColor(148, 163, 184); // slate-400
                doc.setFontSize(9);
                doc.text(field.label, margin, cursorY);

                doc.setFont("helvetica", field.isBold ? "bold" : "normal");
                doc.setTextColor(field.isStatus ? (receipt.status === 'paid' ? 16 : 180) : 30, field.isStatus ? (receipt.status === 'paid' ? 124 : 130) : 41, field.isStatus ? (receipt.status === 'paid' ? 16 : 30) : 59);
                doc.setFontSize(11);
                doc.text(String(field.value), margin + 40, cursorY);
                cursorY += 12;
            });

            // Summary Table Style
            cursorY += 10;
            doc.setFillColor(37, 99, 235);
            doc.rect(margin, cursorY, textWidth, 12, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont("helvetica", "bold");
            doc.text("RESUMEN DE TRANSACCIÓN", margin + 5, cursorY + 8);

            cursorY += 12;
            doc.setDrawColor(37, 99, 235);
            doc.rect(margin, cursorY, textWidth, 25);

            doc.setTextColor(30, 41, 59);
            doc.setFontSize(14);
            doc.text("TOTAL:", margin + 10, cursorY + 16);
            doc.setFontSize(18);
            doc.text(`$${receipt.amount.toLocaleString('es-CO')}`, pageWidth - margin - 50, cursorY + 16);

            // Footer
            const footerY = 270;
            doc.setDrawColor(226, 232, 240);
            doc.line(margin, footerY, pageWidth - margin, footerY);
            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184);
            doc.text("Este es un comprobante de pago generado por el Despacho Digital SarAI.", margin, footerY + 8);
            doc.text("Documento oficial con validez jurídica interna.", margin, footerY + 13);

            doc.save(`${receipt.receipt_number || 'recibo'}_sarai.pdf`);
            toast({ title: "Recibo descargado", description: "El PDF se ha generado correctamente." });
        } catch (error) {
            console.error("Error generating receipt PDF:", error);
            toast({ title: "Error", description: "No se pudo generar el PDF del recibo", variant: "destructive" });
        }
    };

    return (
        <div className="animate-fade-in-up">
            <PageHeader title="Historial de Recibos" icon={ReceiptIcon} />
            <Card className="border border-gray-100/80 shadow-sm">
                <div className="w-full overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100">
                                <TableHead className="text-gray-500 font-medium">Nº Recibo</TableHead>
                                <TableHead className="text-gray-500 font-medium">Cliente</TableHead>
                                <TableHead className="text-gray-500 font-medium">Fecha</TableHead>
                                <TableHead className="text-gray-500 font-medium">Monto</TableHead>
                                <TableHead className="text-gray-500 font-medium">Estado</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {receipts.map(r => (
                                <TableRow key={r.id} className="border-gray-100 hover:bg-gray-50/50 transition-colors">
                                    <TableCell className="font-medium">{r.receipt_number}</TableCell>
                                    <TableCell className="text-gray-500">{r.client_name}</TableCell>
                                    <TableCell className="text-gray-500">{new Date(r.date).toLocaleDateString()}</TableCell>
                                    <TableCell className="font-semibold">${r.amount.toLocaleString()}</TableCell>
                                    <TableCell><Badge className={r.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}>{r.status === 'paid' ? 'Pagado' : 'Pendiente'}</Badge></TableCell>
                                    <TableCell><Button variant="ghost" size="icon" className="rounded-xl" onClick={() => handleDownloadReceipt(r)}><Download className="h-4 w-4 text-gray-400" /></Button></TableCell>
                                </TableRow>
                            ))}
                            {!loading && receipts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-400">No hay recibos generados.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    )
};

export const CrearReciboManualPage = () => {
    const { addReceipt, clients } = useDespachos();
    const [formData, setFormData] = useState({ client_id: "", amount: "", concept: "" });

    const handleSubmit = async () => {
        if (!formData.client_id || !formData.amount) return;
        const client = clients.find(c => c.id === formData.client_id);
        await addReceipt({
            client_id: formData.client_id,
            client_name: client?.name,
            amount: parseFloat(formData.amount),
            concept: formData.concept,
            date: new Date().toISOString(),
            status: 'paid',
            receipt_number: `REC-${Math.floor(Math.random() * 10000)}`
        });
        setFormData({ client_id: "", amount: "", concept: "" });
    };

    return (
        <div className="max-w-2xl mx-auto animate-fade-in-up">
            <PageHeader title="Nuevo Recibo" icon={ReceiptIcon} />
            <Card className="border border-gray-100/80 shadow-sm">
                <CardContent className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <label className="text-sm font-medium text-gray-600">Cliente</label>
                            <Select onValueChange={(val) => setFormData({ ...formData, client_id: val })}>
                                <SelectTrigger className="rounded-xl">
                                    <SelectValue placeholder="Seleccionar cliente" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium text-gray-600">Fecha</label>
                            <Input className="rounded-xl" type="date" disabled value={new Date().toISOString().split('T')[0]} />
                        </div>
                    </div>
                    <div className="grid gap-2">
                        <label className="text-sm font-medium text-gray-600">Concepto</label>
                        <Input className="rounded-xl" placeholder="Honorarios por..." value={formData.concept} onChange={(e) => setFormData({ ...formData, concept: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <label className="text-sm font-medium text-gray-600">Monto</label>
                            <Input className="rounded-xl" type="number" placeholder="0.00" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium text-gray-600">Estado</label>
                            <Input className="rounded-xl" disabled value="Pagado" />
                        </div>
                    </div>
                    <Button className="w-full mt-4 rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md" onClick={handleSubmit}>Generar Recibo</Button>
                </CardContent>
            </Card>
        </div>
    )
};

// --- Others ---

export const GraficosPage = () => {
    const { receipts, processes } = useDespachos();
    const { "*": subPath } = useParams();

    const getTitle = () => {
        switch (subPath) {
            case 'procesos': return "Estadísticas de Procesos";
            case 'tramites': return "Estadísticas de Trámites";
            case 'recibos': return "Análisis de Recibos y Facturación";
            case 'clientes': return "Métricas de Clientes";
            default: return "Estadísticas del Despacho";
        }
    };

    // Aggregating monthly income
    const monthlyIncomeData = useMemo(() => {
        const last6Months = Array.from({ length: 6 }, (_, i) => {
            const d = new Date();
            d.setMonth(d.getMonth() - (5 - i));
            return {
                name: d.toLocaleDateString('es-ES', { month: 'short' }),
                month: d.getMonth(),
                year: d.getFullYear(),
                total: 0
            };
        });

        receipts.forEach(r => {
            const date = new Date(r.date);
            const m = last6Months.find(item => item.month === date.getMonth() && item.year === date.getFullYear());
            if (m) m.total += (Number(r.amount) || 0);
        });

        return last6Months;
    }, [receipts]);

    // Aggregating process distribution
    const processDistributionData = useMemo(() => {
        const counts: Record<string, number> = {};
        processes.forEach(p => {
            const status = p.status || 'Desconocido';
            counts[status] = (counts[status] || 0) + 1;
        });

        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    }, [processes]);

    const COLORS = ['#1a73e8', '#489ba6', '#7c3aed', '#ec4899', '#f59e0b'];

    return (
        <div className="animate-fade-in-up">
            <PageHeader title={getTitle()} icon={BarChartIcon} />
            <div className="grid gap-5 md:grid-cols-2">
                <Card className="border border-gray-100/80 shadow-sm hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center"><BarChartIcon className="h-4 w-4 text-white" /></div>
                            Ingresos Mensuales (Últimos 6 meses)
                        </CardTitle>
                        <CardDescription>Resumen de facturación mensual en pesos</CardDescription>
                    </CardHeader>
                    <CardContent className="h-80 pt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={monthlyIncomeData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                    formatter={(value: number) => [`$${value.toLocaleString()}`, 'Total']}
                                />
                                <Bar dataKey="total" fill="url(#colorTotal)" radius={[6, 6, 0, 0]} />
                                <defs>
                                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#1a73e8" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#1a73e8" stopOpacity={0.3} />
                                    </linearGradient>
                                </defs>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                <Card className="border border-gray-100/80 shadow-sm hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center"><PieChartIcon className="h-4 w-4 text-white" /></div>
                            Distribución de Casos por Estado
                        </CardTitle>
                        <CardDescription>Carga de trabajo actual por etapa procesal</CardDescription>
                    </CardHeader>
                    <CardContent className="h-80 pt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={processDistributionData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {processDistributionData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                />
                                <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            <Card className="mt-5 border border-gray-100/80 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-base font-semibold">Resumen de Actividad Económica</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="p-4 rounded-2xl bg-blue-50/50 border border-blue-100">
                            <p className="text-xs text-blue-600 font-bold uppercase mb-1">Total Facturado</p>
                            <h3 className="text-2xl font-bold text-legal-navy">
                                ${receipts.reduce((acc, curr) => acc + curr.amount, 0).toLocaleString()}
                            </h3>
                        </div>
                        <div className="p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100">
                            <p className="text-xs text-emerald-600 font-bold uppercase mb-1">Gasto Promedio por Caso</p>
                            <h3 className="text-2xl font-bold text-legal-navy">
                                $250.000 <span className="text-xs font-normal text-gray-400">est.</span>
                            </h3>
                        </div>
                        <div className="p-4 rounded-2xl bg-amber-50/50 border border-amber-100">
                            <p className="text-xs text-amber-600 font-bold uppercase mb-1">Crecimiento Mensual</p>
                            <h3 className="text-2xl font-bold text-legal-navy">
                                +12% <span className="text-xs font-normal text-gray-400">vs mes anterior</span>
                            </h3>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

// --- LEPD Templates ---

const LEPD_TEMPLATES = {
    "Política de Privacidad Integral": `# POLÍTICA DE PRIVACIDAD INTEGRAL\n\n## 1. IDENTIDAD Y DOMICILIO DEL RESPONSABLE\nEl Responsable de la protección de sus datos personales es [Nombre del Despacho/Abogado], con domicilio en [Dirección completa].\n\n## 2. DATOS PERSONALES QUE SERÁN SOMETIDOS A TRATAMIENTO\nPara cumplir con las finalidades señaladas en la presente Política de Privacidad, trataremos las siguientes categorías de datos:\n- Datos de identificación y contacto.\n- Datos laborales y académicos.\n- Datos patrimoniales y/o financieros.\n- Datos de características físicas (en caso de ser necesario para el proceso legal).\n\n## 3. FINALIDADES DEL TRATAMIENTO\nLos datos personales que recabamos de usted los utilizaremos para las siguientes finalidades necesarias para el servicio solicitado:\n- Prestación de servicios legales y representación jurídica.\n- Gestión de expedientes y trámites ante autoridades.\n- Facturación y cobranza.\n\n## 4. TRANSFERENCIA DE DATOS\nSus datos personales pueden ser compartidos con autoridades judiciales, peritos o contrapartes únicamente en la medida necesaria para el ejercicio de sus derechos legales.\n\n## 5. DERECHOS ARCO\nUsted tiene derecho a conocer qué datos personales tenemos de usted, para qué los utilizamos y las condiciones del uso que les damos (Acceso). Asimismo, es su derecho solicitar la corrección de su información personal en caso de que esté desactualizada, sea inexacta o incompleta (Rectificación); que la eliminemos de nuestros registros o bases de datos cuando considere que la misma no está siendo utilizada conforme a los principios, deberes y obligaciones previstos en la normativa (Cancelación); así como oponerse al uso de sus datos personales para fines específicos (Oposición).\n\nPara el ejercicio de cualquiera de los derechos ARCO, usted deberá presentar la solicitud respectiva a través de [Correo electrónico/Teléfono].\n\n## 6. CAMBIOS A LA POLÍTICA DE PRIVACIDAD\nLa presente Política de Privacidad puede sufrir modificaciones, cambios o actualizaciones derivadas de nuevos requerimientos legales.`,
    "Aviso de Privacidad": `# AVISO DE PRIVACIDAD\n\n[Nombre del Despacho], con domicilio en [Dirección], le informa que sus datos personales serán utilizados para:\n1. Proveer los servicios legales solicitados.\n2. Informarle sobre cambios en los mismos.\n3. Evaluar la calidad del servicio que le brindamos.\n\nPara las finalidades antes mencionadas, requerimos obtener datos personales tales como: Nombre, Teléfono, Correo electrónico, entre otros.\n\nUsted tiene derecho de acceder, rectificar y cancelar sus datos personales, así como de oponerse al tratamiento de los mismos o revocar el consentimiento que para tal fin nos haya otorgado.\n\nPara conocer el procedimiento y requisitos para el ejercicio de los derechos ARCO, usted podrá ponerse en contacto con nuestro Departamento de Datos Personales en [Correo electrónico].\n\nFecha de última actualización: [Fecha]`,
    "Manual de Políticas y Procedimientos": `# MANUAL DE POLÍTICAS Y PROCEDIMIENTOS DE PRIVACIDAD\n\n## OBJETIVO\nEstablecer las directrices para el tratamiento legítimo, controlado e informado de los datos personales en [Nombre del Despacho].\n\n## ALCANCE\nEste manual es de observancia obligatoria para todo el personal del despacho.\n\n## PROCEDIMIENTOS\n1. **Recolección:** Se debe informar al titular del Aviso de Privacidad antes de recabar cualquier dato.\n2. **Almacenamiento:** Los datos se guardarán en sistemas protegidos con acceso restringido.\n3. **Ejercicio de Derechos ARCO:** Las solicitudes se recibirán vía email y se responderán en un plazo máximo de [Número] días.\n4. **Respuesta a Incidentes:** En caso de vulneración, se notificará al titular y a la autoridad competente de inmediato.\n\n## MEDIDAS DE SEGURIDAD\n- Uso de contraseñas robustas.\n- Encriptación de archivos sensibles.\n- Auditorías periódicas de acceso a bases de datos.`,
    "Consentimiento Informado": `# CONSENTIMIENTO INFORMADO PARA EL TRATAMIENTO DE DATOS PERSONALES\n\nYo, [Nombre del Titular], con identificación [ID], por medio del presente documento, otorgo mi consentimiento libre, previo, expreso e informado a [Nombre del Despacho] para que realice el tratamiento de mis datos personales, incluyendo aquellos que puedan ser considerados como sensibles.\n\n## FINALIDADES AUTORIZADAS\nEl tratamiento de mis datos tendrá como finalidades:\n- La prestación de servicios de asesoría y representación legal.\n- El envío de notificaciones relacionadas con mi proceso.\n- El registro en la base de datos de clientes del despacho.\n\n## DERECHOS DEL TITULAR\nDeclaro que he sido informado sobre mi derecho a:\n1. Conocer, actualizar y rectificar mis datos personales.\n2. Solicitar prueba de la autorización otorgada.\n3. Ser informado respecto del uso dado a mis datos.\n4. Revocar la autorización y/o solicitar la supresión del dato cuando no se respeten los principios, derechos y garantías legales.\n\nEn constancia de lo anterior, firmo el presente documento.\n\n---\nFirma del Titular: __________________________\nFecha: [Fecha]`
};

export const LepdPage = () => {
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState<{ name: string, content: string } | null>(null);
    const { user } = useAuth();
    const { toast } = useToast();

    const openDoc = (name: string) => {
        const content = LEPD_TEMPLATES[name as keyof typeof LEPD_TEMPLATES] || "";
        setSelectedDoc({ name, content });
        setIsEditorOpen(true);
    };

    const handleSave = async (name: string, content: string) => {
        if (!user) return;
        try {
            const fileName = name.endsWith('.docx') ? name : `${name}.docx`;
            const path = `${user.id}/${fileName}`;
            const blob = new Blob([content], { type: 'text/plain' });

            const { error } = await supabase.storage.from('gestion_misarchivos').upload(path, blob, {
                upsert: true
            });

            if (error) throw error;

            toast({
                title: "Documento Guardado",
                description: `El documento ${fileName} ha sido guardado en Mis Archivos.`,
            });
            setIsEditorOpen(false);
        } catch (error) {
            console.error("Error saving LEPD document:", error);
            toast({
                title: "Error",
                description: "No se pudo guardar el documento.",
                variant: "destructive"
            });
        }
    };

    return (
        <div className="animate-fade-in-up">
            <PageHeader title="Ley de Protección de Datos (LEPD)" description="Gestión de cumplimiento normativo" icon={Lock} />
            <Card className="border border-gray-100/80 shadow-sm">
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                            <CheckCircle2 className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-emerald-800">Estado de Cumplimiento: Óptimo</h3>
                            <p className="text-sm text-emerald-600">Todas las políticas están actualizadas y vigentes.</p>
                        </div>
                    </div>

                    <div className="space-y-2 mt-6">
                        <h3 className="font-semibold text-gray-900">Documentación Obligatoria</h3>
                        {["Política de Privacidad Integral", "Aviso de Privacidad", "Manual de Políticas y Procedimientos", "Consentimiento Informado"].map((doc, index) => (
                            <div key={doc} className={`flex items-center justify-between p-3.5 border border-gray-100 rounded-xl hover:bg-blue-50/30 hover:border-blue-200 transition-all duration-200 animate-fade-in-up stagger-${index + 1}`}>
                                <span className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center"><FileText className="h-3.5 w-3.5 text-white" /></div>
                                    <span className="text-sm font-medium text-gray-700">{doc}</span>
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-xl"
                                    onClick={() => openDoc(doc)}
                                >
                                    Ver / Editar
                                </Button>
                            </div>
                        ))}
                    </div>

                    {isEditorOpen && selectedDoc && (
                        <div className="fixed inset-0 z-50 bg-white">
                            <DocumentEditor
                                fileName={selectedDoc.name + ".docx"}
                                initialContent={selectedDoc.content}
                                onClose={() => setIsEditorOpen(false)}
                                onSave={handleSave}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export const MisRecursosSharedPage = () => (
    <div className="animate-fade-in-up">
        <PageHeader title="Mis Recursos Compartidos" icon={Share2} />
        <Card className="border border-gray-100/80 shadow-sm">
            <CardContent className="p-12 text-center">
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Share2 className="h-7 w-7 text-gray-300" />
                </div>
                <p className="text-gray-400 text-sm">No has compartido recursos aún.</p>
            </CardContent>
        </Card>
    </div>
);

export const AllRecursosSharedPage = () => (
    <div className="animate-fade-in-up">
        <PageHeader title="Todos los Recursos" description="Recursos compartidos conmigo y por mí" icon={Share2} />
        <Card className="border border-gray-100/80 shadow-sm">
            <CardContent className="p-12 text-center">
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Share2 className="h-7 w-7 text-gray-300" />
                </div>
                <p className="text-gray-400 text-sm">Lista completa de recursos compartidos...</p>
            </CardContent>
        </Card>
    </div>
);

export const ConfiguracionPage = () => {
    const { toast } = useToast();

    // SMTP States
    const [smtpStatus, setSmtpStatus] = useState<legalEmailsApi.SmtpStatus | null>(null);
    const [loadingStatus, setLoadingStatus] = useState(false);

    const [smtpHost, setSmtpHost] = useState("");
    const [smtpPort, setSmtpPort] = useState(587);
    const [smtpUser, setSmtpUser] = useState("");
    const [smtpPass, setSmtpPass] = useState("");
    const [smtpFromEmail, setSmtpFromEmail] = useState("");
    const [smtpFromName, setSmtpFromName] = useState("");

    const [isSaving, setIsSaving] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [isVerifying, setIsVerifying] = useState(false);
    const [showOtpField, setShowOtpField] = useState(false);

    // Single Sender States
    const [sendersList, setSendersList] = useState<legalEmailsApi.VerifiedSender[]>([]);
    const [loadingSenders, setLoadingSenders] = useState(false);
    const [newSenderEmail, setNewSenderEmail] = useState("");
    const [newSenderName, setNewSenderName] = useState("");
    const [isRegisteringSender, setIsRegisteringSender] = useState(false);

    const [senderOtp, setSenderOtp] = useState("");
    const [verifyingSenderEmail, setVerifyingSenderEmail] = useState<string | null>(null);
    const [isVerifyingSender, setIsVerifyingSender] = useState(false);

    const loadSmtpStatus = async () => {
        setLoadingStatus(true);
        try {
            const status = await legalEmailsApi.getSmtpStatus();
            setSmtpStatus(status);
            if (status.configured) {
                setSmtpHost(status.smtp_from_email ? "SMTP Configurado" : "");
                setSmtpFromEmail(status.smtp_from_email || "");
                setSmtpFromName(status.smtp_from_name || "");
            }
        } catch (err) {
            console.error("Error loading SMTP status:", err);
        } finally {
            setLoadingStatus(false);
        }
    };

    const loadSenders = async () => {
        setLoadingSenders(true);
        try {
            const list = await legalEmailsApi.getVerifiedSenders();
            // Filtrar remitentes del sistema
            setSendersList(list.filter(s => s.type !== "resend_system"));
        } catch (err) {
            console.error("Error loading senders:", err);
        } finally {
            setLoadingSenders(false);
        }
    };

    const handleRegisterSender = async () => {
        if (!newSenderEmail.trim()) {
            toast({ variant: "destructive", title: "Email requerido", description: "Ingresa el email a verificar." });
            return;
        }
        setIsRegisteringSender(true);
        try {
            const res = await legalEmailsApi.registerSingleSender(newSenderEmail.trim(), newSenderName.trim() || undefined);
            toast({ title: "OTP Enviado", description: res.message || "Código enviado al correo de destino." });
            setVerifyingSenderEmail(newSenderEmail.trim());
        } catch (err: any) {
            toast({ variant: "destructive", title: "Error al registrar", description: err.message || "No se pudo registrar la dirección." });
        } finally {
            setIsRegisteringSender(false);
        }
    };

    const handleVerifySender = async () => {
        if (!verifyingSenderEmail || senderOtp.length !== 6) {
            toast({ variant: "destructive", title: "Datos inválidos", description: "Código de 6 dígitos requerido." });
            return;
        }
        setIsVerifyingSender(true);
        try {
            await legalEmailsApi.verifySingleSender(verifyingSenderEmail, senderOtp);
            toast({ title: "Email Verificado", description: `El correo ${verifyingSenderEmail} ha sido activado.` });
            setVerifyingSenderEmail(null);
            setSenderOtp("");
            setNewSenderEmail("");
            setNewSenderName("");
            loadSenders();
        } catch (err: any) {
            toast({ variant: "destructive", title: "Verificación fallida", description: err.message || "El código es incorrecto." });
        } finally {
            setIsVerifyingSender(false);
        }
    };

    useEffect(() => {
        loadSmtpStatus();
        loadSenders();
    }, []);

    const handleSaveSmtp = async () => {
        if (!smtpHost || !smtpPort || !smtpUser || !smtpPass || !smtpFromEmail) {
            toast({ variant: "destructive", title: "Campos obligatorios", description: "Por favor, ingresa los datos del servidor SMTP." });
            return;
        }
        setIsSaving(true);
        try {
            const res = await legalEmailsApi.setupSmtp({
                smtp_host: smtpHost,
                smtp_port: Number(smtpPort),
                smtp_username: smtpUser,
                smtp_password: smtpPass,
                smtp_from_email: smtpFromEmail,
                smtp_from_name: smtpFromName || undefined
            });
            toast({ title: "Código OTP Enviado", description: res.message || "Código enviado al correo de remitente." });
            setShowOtpField(true);
        } catch (err: any) {
            toast({ variant: "destructive", title: "Error de configuración", description: err.message || "No se pudo conectar al servidor de correo." });
        } finally {
            setIsSaving(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (verificationCode.length !== 6) {
            toast({ variant: "destructive", title: "Código inválido", description: "El código OTP debe ser de 6 dígitos numéricos." });
            return;
        }
        setIsVerifying(true);
        try {
            await legalEmailsApi.verifySmtp(verificationCode);
            toast({ title: "Servidor Validado", description: "Tu correo personalizado ya está configurado y habilitado." });
            setShowOtpField(false);
            setVerificationCode("");
            loadSmtpStatus();
        } catch (err: any) {
            toast({ variant: "destructive", title: "Código incorrecto", description: err.message || "Verifica el código ingresado e intenta nuevamente." });
        } finally {
            setIsVerifying(false);
        }
    };

    return (
        <div className="max-w-3xl animate-fade-in-up">
            <PageHeader title="Configuración del Despacho" icon={Settings} />
            <Tabs defaultValue="general">
                <TabsList className="mb-5 bg-gray-100 p-1 rounded-xl h-auto">
                    <TabsTrigger value="general" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">General</TabsTrigger>
                    <TabsTrigger value="smtp" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">Correo Saliente (SMTP)</TabsTrigger>
                    <TabsTrigger value="usuarios" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">Usuarios</TabsTrigger>
                </TabsList>
                <TabsContent value="general">
                    <Card className="border border-gray-100/80 shadow-sm">
                        <CardHeader><CardTitle className="text-base font-semibold">Información del Despacho</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid gap-2">
                                <label className="text-sm font-medium text-gray-600">Nombre del Despacho</label>
                                <Input className="rounded-xl" defaultValue="Mi Despacho Legal" />
                            </div>
                            <div className="grid gap-2">
                                <label className="text-sm font-medium text-gray-600">Dirección</label>
                                <Input className="rounded-xl" defaultValue="Calle 123 # 45-67" />
                            </div>
                            <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md">Guardar Cambios</Button>
                        </CardContent>
                    </Card>
                </TabsContent>
                <TabsContent value="smtp">
                    <Card className="border border-gray-100/80 shadow-sm">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-base font-semibold">Configuración de Remitente Personalizado</CardTitle>
                                    <CardDescription>Configura tu propio servidor de correo de salida para enviar notificaciones desde tu email real.</CardDescription>
                                </div>
                                {loadingStatus ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                                ) : smtpStatus?.is_verified ? (
                                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold text-xs rounded-full">
                                        ✓ Activo (SMTP)
                                    </Badge>
                                ) : smtpStatus?.configured ? (
                                    <Badge className="bg-amber-50 text-amber-700 border border-amber-200 font-bold text-xs rounded-full animate-pulse">
                                        ⚠️ Verificación Pendiente
                                    </Badge>
                                ) : (
                                    <Badge className="bg-gray-100 text-gray-500 border border-gray-200 text-xs rounded-full">
                                        No Configurado
                                    </Badge>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* SMTP Config Form */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Servidor SMTP *</label>
                                    <Input
                                        className="rounded-xl"
                                        placeholder="ej: smtp.gmail.com"
                                        value={smtpHost}
                                        onChange={(e) => setSmtpHost(e.target.value)}
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Puerto SMTP *</label>
                                    <Input
                                        type="number"
                                        className="rounded-xl"
                                        placeholder="ej: 465 o 587"
                                        value={smtpPort}
                                        onChange={(e) => setSmtpPort(Number(e.target.value))}
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Usuario SMTP (Email) *</label>
                                    <Input
                                        className="rounded-xl"
                                        placeholder="ej: abogado@midominio.com"
                                        value={smtpUser}
                                        onChange={(e) => setSmtpUser(e.target.value)}
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Contraseña SMTP *</label>
                                    <Input
                                        type="password"
                                        className="rounded-xl"
                                        placeholder="Ingresa la contraseña o token de aplicación"
                                        value={smtpPass}
                                        onChange={(e) => setSmtpPass(e.target.value)}
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Email Remitente (From) *</label>
                                    <Input
                                        className="rounded-xl"
                                        placeholder="ej: notificaciones@midominio.com"
                                        value={smtpFromEmail}
                                        onChange={(e) => setSmtpFromEmail(e.target.value)}
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nombre del Remitente (Visual)</label>
                                    <Input
                                        className="rounded-xl"
                                        placeholder="ej: Abogado Juan Pérez"
                                        value={smtpFromName}
                                        onChange={(e) => setSmtpFromName(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* OTP Validation Section */}
                            {showOtpField && (
                                <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl space-y-3 mt-4">
                                    <div className="flex items-start gap-2.5">
                                        <AlertCircle className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-bold text-indigo-900">Verificación en Proceso</p>
                                            <p className="text-xs text-indigo-700">Se ha enviado un código de 6 dígitos a {smtpFromEmail}. Por favor ingrésalo abajo para validar la cuenta.</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 max-w-xs items-center">
                                        <Input
                                            className="rounded-xl font-mono text-center tracking-widest text-lg h-10 bg-white"
                                            maxLength={6}
                                            placeholder="000000"
                                            value={verificationCode}
                                            onChange={(e) => setVerificationCode(e.target.value)}
                                        />
                                        <Button
                                            className="rounded-xl bg-indigo-600 hover:bg-indigo-700 font-bold"
                                            onClick={handleVerifyOtp}
                                            disabled={isVerifying}
                                        >
                                            {isVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Validar"}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <div className="pt-2 flex justify-end">
                                <Button
                                    className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md font-bold gap-2"
                                    onClick={handleSaveSmtp}
                                    disabled={isSaving}
                                >
                                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar y Enviar Código"}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Tarjeta 2: Remitentes Rápidos (Single Senders) */}
                    <Card className="border border-gray-100/80 shadow-sm mt-5">
                        <CardHeader>
                            <CardTitle className="text-base font-semibold">Remitentes de Correo Único (Rápido sin SMTP)</CardTitle>
                            <CardDescription>
                                Verifica direcciones individuales enviándoles un código OTP. Los correos se enviarán con la firma de visualización de tu nombre a través de la pasarela segura del sistema (DMARC compliant).
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-5">
                            {/* Formulario de registro rápido */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                                <div className="grid gap-1.5">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Email a Verificar *</label>
                                    <Input
                                        type="email"
                                        className="rounded-xl bg-white"
                                        placeholder="ej: abogado@gmail.com"
                                        value={newSenderEmail}
                                        onChange={(e) => setNewSenderEmail(e.target.value)}
                                        disabled={!!verifyingSenderEmail}
                                    />
                                </div>
                                <div className="grid gap-1.5">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nombre del Remitente</label>
                                    <Input
                                        className="rounded-xl bg-white"
                                        placeholder="ej: Dr. Juan Pérez"
                                        value={newSenderName}
                                        onChange={(e) => setNewSenderName(e.target.value)}
                                        disabled={!!verifyingSenderEmail}
                                    />
                                </div>
                                <div className="md:col-span-2 flex justify-end">
                                    <Button
                                        className="rounded-xl bg-indigo-600 hover:bg-indigo-700 font-bold gap-2"
                                        onClick={handleRegisterSender}
                                        disabled={isRegisteringSender || !!verifyingSenderEmail}
                                    >
                                        {isRegisteringSender ? <Loader2 className="h-4 w-4 animate-spin" /> : "Solicitar Código de Verificación"}
                                    </Button>
                                </div>
                            </div>

                            {/* OTP Remitente Único */}
                            {verifyingSenderEmail && (
                                <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl space-y-3">
                                    <div className="flex items-start gap-2.5">
                                        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-bold text-amber-900">Verificación Pendiente</p>
                                            <p className="text-xs text-amber-700">Se ha enviado un código de seguridad a <strong>{verifyingSenderEmail}</strong>. Ingrésalo abajo para validar el correo.</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 max-w-xs items-center">
                                        <Input
                                            className="rounded-xl font-mono text-center tracking-widest text-lg h-10 bg-white"
                                            maxLength={6}
                                            placeholder="000000"
                                            value={senderOtp}
                                            onChange={(e) => setSenderOtp(e.target.value)}
                                        />
                                        <Button
                                            className="rounded-xl bg-amber-600 hover:bg-amber-700 font-bold"
                                            onClick={handleVerifySender}
                                            disabled={isVerifyingSender}
                                        >
                                            {isVerifyingSender ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar Correo"}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Listado de remitentes */}
                            <div className="space-y-2.5">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Remitentes Registrados</label>
                                {loadingSenders ? (
                                    <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
                                ) : sendersList.length > 0 ? (
                                    <div className="space-y-2">
                                        {sendersList.map((s) => (
                                            <div key={s.email} className="flex items-center justify-between p-3.5 border border-gray-100 rounded-xl bg-white shadow-sm">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                                                        <Mail className="h-4 w-4" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-gray-800">{s.name || "Remitente"}</p>
                                                        <p className="text-xs text-gray-400 font-mono">{s.email}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge className={s.is_verified ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-amber-50 text-amber-700 border-amber-100"}>
                                                        {s.is_verified ? "Verificado" : "Pendiente"}
                                                    </Badge>
                                                    <Badge variant="outline" className="text-[10px] font-semibold text-gray-400">
                                                        {s.type === 'smtp' ? 'SMTP' : 'Sistema'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-400 text-sm border border-dashed border-gray-200 rounded-xl">
                                        No tienes remitentes rápidos verificados. Registra el primero arriba.
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
                <TabsContent value="usuarios">
                    <Card className="border border-gray-100/80 shadow-sm">
                        <CardHeader><CardTitle className="text-base font-semibold">Gestión de Usuarios</CardTitle></CardHeader>
                        <CardContent>
                            <p className="text-gray-400 text-sm">Configuración de usuarios...</p>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

